import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../../components/shared/Checkbox';
import { toast } from '../../components/shared/Toast';
import {
  listMail, bulkPatchMail, patchMail, getMailCounts,
  listMailFolders, createMailFolder, updateMailFolder, deleteMailFolder,
  markFolderRead, emptyFolder, purgeMail, moveMailBySender,
  type MailCounts, type MailFolder, type MailFolderNode, type MailListItem, type MailSort, type MailOrder,
} from '../../api/mail';
import {
  ArrowLeft, ArrowUpDown, Bot, Check, ChevronDown, Edit, FileText, Inbox, MoreVertical,
  Paperclip, Search, Send, Settings, Shield, Star, Trash2, User, X,
} from '../../icons';
import { EmptyState } from '../../components/shared/EmptyState';
// 화면 머리 — 근태 · 인사 · 결재와 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/shared/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { MailDetail } from './MailDetail';
// 보낸사람 이름을 누르면 뜨는 사람 카드 (2026-09-08 · 정본 F3-2).
import { PersonCard } from '../../components/PersonCard/PersonCard';
import { ContactPickModal } from './ContactPickModal';
import { externalChip } from './RecipientPicker';
import { MailComposePage, type MailComposeNavState } from './MailComposePage';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
// 조직도 팝업의 "메일쓰기" 가 남겨 둔 받는사람을 여기서 꺼내 쓴다(한 번만).
import { useMailComposeStore } from '../../store/mailComposeStore';
// 「팝업쓰기」(2026-09-05) — 화면을 떠나지 않고 여는 판. 알맹이는 화면판과 같은 것이다.
import { ComposeModal, type ComposePrefill } from './ComposeModal';
import { useAuthStore } from '../../store/authStore';
import { useMailPrefs, simplifySubject, invalidateMailPrefs } from './mailPrefs';
// 보기 레이어 (2026-08-21) — 열림 방식 · 요약 4칸 · 줄 위 작업 · 묶어 보기 · 팝업보기.
import { DEFAULT_MAIL_VIEW, loadMailView, saveMailView, type MailGroupBy, type MailOpenMode } from './mailView';
// 밀도는 「환경설정 > 기본환경」과 같은 값이라 서버에도 적는다(migration 158).
import { patchMySettings, type MailListDensity } from '../../api/users';
import { groupMail, type MailDateBucket } from './mailGroup';
import { useDragSelect, DRAG_ROW_ATTR } from './useDragSelect';
import { MailStatRow, type MailStatCounts, type MailStatKey } from './MailStatRow';
import { MailRowActions, type MailRowMode } from './MailRowActions';
import { MailFilterMenu, MailOpenMenu } from './MailViewMenu';
import { MailPopout } from './MailPopout';
import { MailSplitter } from './MailSplitter';
import { MailSelectionBar } from './MailSelectionBar';
import { MailFolderTree, type FolderMenuAction } from './MailFolderTree';
import { MailConfirmDialog } from './MailConfirmDialog';
import { MailListHead } from './MailListHead';
import { MailPager, type MailPageSize } from './MailPager';
import { MailQuotaBar } from './MailQuotaBar';
// 환경설정은 제 화면이 아니라 **이 셸의 오른쪽 칸**이다 (2026-09-05).
import { MailSettingsPage } from './MailSettingsPage';
import { notifyMailFoldersChanged, subscribeMailFolders } from './mailFolderBus';
import './MailPage.css';
// ★ MailPage.css 뒤에 실어 필요한 것만 덮는다 — 기존 파일은 한 줄도 고치지 않는다.
import './MailPageView.css';
// ★ 상세 머리(도구 한 줄·켜짐 색·받는사람/참조)는 다시 그 뒤에 실어 덮는다 — 2026-08-22.
import './MailDetailHeader.css';
import './MailListRow.css';
import '../../styles/mail-tag.css';

/**
 * MailPage (Phase 3, P3-MAIL-01) — 시안 line 1252~1306 매핑. 좌측 폴더 sidebar + 중앙 메시지 목록.
 *
 * 본 사이클은 read-only 골격:
 *   - 폴더 5종 (받은편지함 / 중요 / 보낸편지함 / 임시보관함 / 휴지통)
 *   - 메시지 목록 (sender / subject / preview / time / unread dot / star icon)
 *   - 상세 패널 / 메일 작성 / 별표 토글 / 폴더 이동 / 읽음 처리는 후속 atomic.
 */

interface FolderItem {
  id: MailFolder;
  labelKey: MailI18nKey;
  icon: typeof Inbox;
}

// Cycle 54 — folder id 만 SSOT, label 은 tMail() 로 lang-aware (render 시점 결정).
/**
 * 메일함 (2026-08-21 확대).
 *
 * ★ 내게쓴메일함은 **서버가 새것일 때만** 보인다(`serverNew`). 옛 서버는 그 값을
 *   모르고 400 을 내는데, 눌러서 빈 화면이 나오는 것보다 아예 없는 편이 낫다.
 * ★★ **「전체메일」은 2026-09-05 사용자 지시로 뺐다.** 서버의 `folder='all'` 은 그대로 두고
 *    (셈·검색이 그것을 쓴다) **메뉴에서만** 사라진다 — 라우트를 지우면 다른 것이 함께 죽는다.
 */
const FOLDERS: FolderItem[] = [
  { id: 'inbox', labelKey: 'mail.folder.inbox', icon: Inbox },
  { id: 'star', labelKey: 'mail.folder.star', icon: Star },
  { id: 'sent', labelKey: 'mail.folder.sent', icon: Send },
  { id: 'draft', labelKey: 'mail.folder.draft', icon: FileText },
  { id: 'self', labelKey: 'mail.folder.self', icon: User },
  // 스팸함은 휴지통 옆에 둔다 — 둘 다 "치워 둔 것" 이다(2026-08-20).
  { id: 'spam', labelKey: 'mail.folder.spam', icon: Shield },
  { id: 'trash', labelKey: 'mail.folder.trash', icon: Trash2 },
];

/**
 * 목록 정렬 (2026-08-20). 지금까지는 받은 순서 하나로 고정이었다.
 *
 * 보낸메일함·임시보관함에서는 보낸 사람이 늘 본인이고 읽음도 항상 true 라 그 둘은 감춘다
 * (서버는 그런 요청이 와도 날짜로 떨어뜨리지만, 뜻 없는 선택지를 보여줄 이유가 없다).
 */
const SORT_FIELDS: { id: MailSort; key: 'mail.sort.date' | 'mail.sort.sender' | 'mail.sort.subject' | 'mail.sort.unread' }[] = [
  { id: 'date', key: 'mail.sort.date' },
  { id: 'sender', key: 'mail.sort.sender' },
  { id: 'subject', key: 'mail.sort.subject' },
  { id: 'unread', key: 'mail.sort.unread' },
];
const SENDER_SIDE_FOLDERS: MailFolder[] = ['sent', 'draft'];
const SORT_STORAGE_KEY = 'axi.mail.sort.v1';

interface SortPref { sort: MailSort; order: MailOrder }
const DEFAULT_SORT: SortPref = { sort: 'date', order: 'desc' };

/** 메일함마다 따로 기억한다 — 받은메일함은 최신순, 보낸메일함은 제목순처럼 쓰는 방식이 다르다. */
function loadSortPrefs(): Record<string, SortPref> {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, SortPref>) : {};
  } catch { return {}; }
}

/** 시간 묶음 이름 — 묶음 계산(mailGroup)은 bucket 만 주고, 번역은 화면이 한다. */
const BUCKET_LABEL_KEY: Record<MailDateBucket, MailI18nKey> = {
  today: 'mail.bucket.today',
  yesterday: 'mail.bucket.yesterday',
  week: 'mail.bucket.week',
  older: 'mail.bucket.older',
  unknown: 'mail.bucket.unknown',
};

/** 서버가 새것일 때만 보이는 메일함 — 옛 서버는 이 값을 모른다.
 *  ★ 'all' 은 목록에서 빠졌으므로(2026-09-05) 여기서도 뺀다 — 거를 것이 없는 이름을 남겨 두면
 *    다음 사람이 「전체메일이 어디 갔나」를 여기서 찾게 된다. */
const NEW_SERVER_FOLDERS: MailFolder[] = ['self'];

/**
 * 관리 메뉴(모두 읽음·비우기)를 다는 메일함.
 * ★ 보낸메일함·임시보관함은 뺀다 — 보낸 메일은 **남의 편지함에도 있는** 것이라
 *   내가 비운다는 말이 성립하지 않는다.
 */
const MANAGEABLE_FOLDERS: MailFolder[] = ['inbox', 'star', 'self', 'spam', 'trash'];

/** 빠른 조회 넷의 이름 — 제목 줄의 "…만 보는 중" 이 쓴다. */
const STAT_LABEL_KEY: Record<MailStatKey, MailI18nKey> = {
  unread: 'mail.stat.unread',
  star: 'mail.stat.star',
  clip: 'mail.stat.clip',
  to: 'mail.stat.to',
};

/**
 * 화면 머리의 **한 줄 설명** (정본 A3 · 2026-09-08).
 *
 * ★ 「무엇을 보는 곳인가」만 적는다 — 규칙(보관기간 · 자동 삭제)은 **도움말**로 간다(정본 P8).
 * ★ 없는 메일함은 기본 문장을 쓴다(내가 만든 메일함 `my` 는 이름이 사람마다 달라 지어낼 수 없다).
 */
const FOLDER_DESC_KEY: Partial<Record<MailFolder, MailI18nKey>> = {
  inbox: 'mail.head.desc_inbox',
  sent: 'mail.head.desc_sent',
  draft: 'mail.head.desc_draft',
  star: 'mail.head.desc_star',
  self: 'mail.head.desc_self',
  spam: 'mail.head.desc_spam',
  trash: 'mail.head.desc_trash',
  all: 'mail.head.desc_all',
};

const FOLDER_TITLE_KEY: Record<MailFolder, MailI18nKey> = {
  my: 'mail.folder.my',
  all: 'mail.folder.all',
  self: 'mail.folder.self',
  inbox: 'mail.folder.inbox',
  sent: 'mail.folder.sent',
  draft: 'mail.folder.draft',
  star: 'mail.folder.star',
  trash: 'mail.folder.trash',
  spam: 'mail.folder.spam',
};

export function MailPage() {
  const [folder, setFolder] = useState<MailFolder>('inbox');
  /** 「팝업쓰기」로 열려 있는 작성 창. null 이면 안 열려 있다. */
  const [composePop, setComposePop] = useState<{ prefill: ComposePrefill | null } | null>(null);
  const myUser = useAuthStore((st) => st.user);
  /** 조직도 팝업이 담아 둔 「이 사람에게 쓰기」 — 「팝업쓰기」일 때 여기서 꺼낸다. */
  const consumePendingTo = useMailComposeStore((st) => st.consume);
  const [messages, setMessages] = useState<MailListItem[]>([]);
  /*
   * 목록의 지금 값 — 요약 숫자를 고칠 때 **바뀌기 전 상태**를 알아야 하기 때문이다.
   * ref 로 두는 이유: 콜백이 messages 를 의존하면 목록이 바뀔 때마다 새로 만들어져
   * 아래 자식들이 통째로 다시 그려진다.
   */
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  /**
   * 다음 쪽 표시 (2026-08-20).
   *
   * ★ 여태 화면은 **첫 쪽만 받고 끝이었다.** 서버는 `nextCursor` 를 계속 주고 있었는데 아무도
   *   쓰지 않았다 — 사내 메일이 몇 통뿐일 때는 티가 안 났지만, 외부 메일 213통을 가져오자
   *   50통에서 잘려 "메일이 다 안 보인다" 가 됐다.
   */
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sortPrefs, setSortPrefs] = useState<Record<string, SortPref>>(loadSortPrefs);
  // 개인 메일 설정 (migration 110) — 값이 오기 전에는 기본값이라 지금까지와 같은 화면이 먼저 뜬다.
  const { prefs: mailPrefs, ready: prefsReady } = useMailPrefs();
  const [firstFolderApplied, setFirstFolderApplied] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  // 여러 개 고르기 (2026-08-20) — 지금까지 상태 변경은 한 통씩만 됐다.
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  /**
   * 영구삭제 확인 대상 — 여러 통(선택바)이든 한 통(줄 아이콘)이든 **같은 창**으로 묻는다.
   * 되돌릴 수 없는 일이라 개수와 무관하게 한 번 확인한다.
   */
  const [purgeAsk, setPurgeAsk] = useState<string[] | null>(null);
  /** 처리 결과를 알리는 창 — 되돌릴 수 없는 일은 한 것을 말해 준다. */
  const [doneNotice, setDoneNotice] = useState<{ title: string; desc: string } | null>(null);
  // 스팸 신고는 **차단까지 따라오는** 동작이라 한 번 묻는다(2026-08-20).
  const [spamAsk, setSpamAsk] = useState(false);
  /**
   * 여러 개를 한 번에 치우기 전에 묻는다 (2026-08-23 사용자 지적 — "알림이 안 뜬다").
   *
   * 한 통을 상세에서 치울 때는 묻는데(#601), **여러 통은 말없이 가 버렸다.** 되돌릴 수 있는
   * 일이라 해도 스무 통이 한 번에 사라지면 사람은 무슨 일이 났는지 모른다.
   * ★ 2026-08-23 후속 — 처음엔 목록 안에 끼는 띠로 만들었는데, 사용자가 "알림이 안 뜬다" 고
   *   다시 짚었다. **목록을 보던 눈에는 목록 안의 띠가 안 보인다.** 그래서 스팸·휴지통 둘 다
   *   가운데 뜨는 팝업(MailConfirmDialog)으로 옮겼다 — 상세·폴더 비우기가 쓰던 그 창이다.
   */
  const [trashAsk, setTrashAsk] = useState(false);
  // 검색 (2026-08-20) — 입력은 즉시 보이고, 서버 호출은 한 박자 늦춘다.
  const [searchInput, setSearchInput] = useState('');
  /**
   * 사람 카드의 「이 사람이 보낸 메일」 · 「내가 보낸 메일」 (2026-09-08 · 정본 F3-2 ④).
   *
   * ★ 새 화면을 만들지 않는다 — **지금 있는 찾기 칸**에 주소를 넣고 메일함만 옮긴다.
   *   받은 것이면 받은편지함, 내가 보낸 것이면 보낸편지함이다.
   * ★ 찾기 칸에도 같은 값을 넣는다 — 안 넣으면 목록만 바뀌고 «왜 이 목록인지»가 화면에 없다.
   */
  /**
   * 사람 카드에서 「주소록에 추가」 (2026-09-08 사용자 지적).
   *
   * ★★★ **화면을 옮기지 않는다.** 처음엔 `/contacts?add=…` 로 보냈는데, 메일을 보다가
   *    담는 일이라 목록에서 튕겨 나가면 하던 일이 끊긴다 — 담고 나서 읽던 메일로
   *    돌아올 방법도 없다. 담기는 **보던 자리에서** 끝난다.
   * ★ 상세와 **같은 팝업**(E1-4)을 한 줄짜리로 연다 — 담는 자리가 둘이면 규칙도 둘이 된다.
   */
  const [contactAdd, setContactAdd] = useState<{ email: string; name: string | null } | null>(null);
  /* 공유 주소록에 **담기**는 모두 (2026-09-15 · 서버 #1374 — 종전: 관리자만). 고치기 · 지우기는 주소록 화면에서 관리자만. */
  const canShareContacts = true;

  const searchByPerson = useCallback((email: string, dir: 'from' | 'to') => {
    setSearchInput(email);
    setQuery(email);
    setFolder(dir === 'from' ? 'inbox' : 'sent');
  }, []);

  const [query, setQuery] = useState('');
  const [blockSender, setBlockSender] = useState(true);

  /* ── 보기 레이어 (2026-08-21) ─────────────────────────────────────────
   * 방식(openMode)은 **설정**이고 상태(detailState)는 **지금 모습**이다. 두 값을 따로 든다 —
   * 닫혀 있을 때 상세가 어느 방향으로 물러나 있어야 하는지는 방식이 정하기 때문이다.
   */
  const [view, setView] = useState(loadMailView);
  const [detailState, setDetailState] = useState<'closed' | 'open' | 'full'>('closed');
  const [statFilters, setStatFilters] = useState<Set<MailStatKey>>(() => new Set());
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [openMenuOpen, setOpenMenuOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const [popout, setPopout] = useState<{ id: string; title: string } | null>(null);
  /* 내가 만드는 메일함 (migration 114) */
  const [folders, setFolders] = useState<MailFolderNode[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  /**
   * 내 메일함이 서버에서 막혔을 때 (2026-08-21).
   *
   * ★ 여태 만들기·이름 바꾸기 실패를 `.catch(() => {})` 로 조용히 삼켰다. 서버가 500 을 내도
   *   화면은 아무 말도 안 해서, 사용자에게는 "눌러도 아무 일이 안 일어난다" 로만 보인다.
   */
  const [folderMsg, setFolderMsg] = useState<MailI18nKey | null>(null);
  const [confirm, setConfirm] = useState<
    | { kind: 'empty'; folder: MailFolder; folderId?: string; name: string; permanent: boolean }
    | { kind: 'delete'; folderId: string; name: string }
    | null
  >(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [movePicker, setMovePicker] = useState<{ ids: string[] } | null>(null);
  /** 이동 창의 「이 보낸 사람의 메일 모두」 (2026-11-02) — 창을 열 때마다 꺼진 채로 시작한다. */
  const [moveBySender, setMoveBySender] = useState(false);
  useEffect(() => { setMoveBySender(false); }, [movePicker]);
  const [basicMenu, setBasicMenu] = useState<MailFolder | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  /**
   * 상세에서 치운 줄 (2026-08-21).
   *
   * ★ 상세의 스팸·휴지통 버튼은 서버에는 잘 닿았는데 **목록에서 그 줄이 그대로 남아** 있었다.
   *   사용자 눈에는 "눌러도 아무 일도 안 일어난다" 로 보인다.
   * ★ 지우지 않고 **감추기만** 한다 — PATCH 가 실패해 되돌아오면(상세가 반대 값을 다시 알려
   *   준다) 그 줄이 다시 보여야 하기 때문이다.
   */
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  // 경계를 끄는 동안에는 전환을 끈다 — 안 끄면 패널이 손끝을 뒤에서 따라온다.
  const [resizing, setResizing] = useState(false);
  // 쪽 번호 (2026-08-21) — 서버가 총 통수를 안 주므로 **받아 온 것**을 쪽으로 자른다.
  const [page, setPage] = useState(1);
  /**
   * 서버가 센 숫자 (2026-08-21). null 이면 **셈 API 가 없는 옛 서버**다 —
   * 그때는 받아 온 것만 세고 `+` 를 붙인다(지금까지와 같다).
   */
  const [counts, setCounts] = useState<MailCounts | null>(null);
  /*
   * 요약 숫자만 다시 묻는 키 (2026-08-26).
   *
   * ★ 읽음·별표·치우기를 해도 숫자가 안 바뀌던 것을 고친다(사용자 지적). 숫자는 **서버가 센 값**
   *   (메일함 전체 기준)이라, 화면에서 한 통을 고쳐도 그대로 남아 있었다. 여태는 다른 메일함으로
   *   **옮겨진 경우에만** 목록째 다시 받아서 그때만 맞았다.
   * ★ 목록까지 다시 받지 않는 이유: 읽음·별표는 줄이 그 자리에 남는데 목록을 새로 받으면
   *   스크롤이 튀고 더 불러온 페이지가 날아간다. 그래서 셈만 따로 묻는다.
   */
  const [countsKey, setCountsKey] = useState(0);

  /**
   * 셈을 다시 묻되 **조금 기다렸다가** (2026-08-26 2차).
   *
   * ★ 곧바로 물으면 **저장이 끝나기 전**이라 서버가 옛 숫자를 돌려주고, 그 값이 방금 화면에서
   *   고친 숫자를 덮는다. 실제로 별을 두 번 켜도 1 로 남고, 다른 메뉴를 갔다 와야 2 가 됐다
   *   (사용자 지적). 저장이 끝날 틈을 준 뒤 한 번만 묻는다 — 연달아 눌러도 마지막 한 번이다.
   */
  const countsTimer = useRef<number | null>(null);
  const refetchCountsSoon = useCallback(() => {
    if (countsTimer.current !== null) window.clearTimeout(countsTimer.current);
    countsTimer.current = window.setTimeout(() => {
      countsTimer.current = null;
      setCountsKey((k) => k + 1);
    }, 700);
  }, []);
  // 화면을 벗어나면 예약을 거둔다.
  useEffect(() => () => {
    if (countsTimer.current !== null) window.clearTimeout(countsTimer.current);
  }, []);

  /**
   * 요약 숫자를 그 자리에서 고치고, 서버에도 다시 묻는다 (2026-08-26).
   *
   * 두 겹인 이유: 서버 왕복을 기다리면 **누른 순간 숫자가 안 바뀌어** 먹통처럼 보인다. 그래서
   * 화면이 먼저 고치고(그 통이 어떤 성질이었는지는 목록이 알고 있다), 곧 도착하는 서버 값이
   * 정답으로 덮어쓴다 — 화면 계산이 틀려도 잠깐이면 교정된다.
   *
   * `removed` = 이 메일함에서 빠진 경우(치우기·스팸 등). 그 통이 갖고 있던 성질만큼 전부 줄인다.
   */
  const adjustCounts = useCallback((
    before: { isRead?: boolean; isStarred?: boolean; attachmentCount?: number; myType?: string } | undefined,
    patch: { isRead?: boolean; isStarred?: boolean },
    removed = false,
  ) => {
    setCounts((cur) => {
      if (!cur) return cur;
      const clamp = (n: number) => Math.max(0, n);
      let unread = cur.unread ?? 0;
      let star = cur.star ?? 0;
      let attach = cur.attach;
      let to = cur.to ?? 0;

      if (removed) {
        if (before && before.isRead === false) unread -= 1;
        if (before?.isStarred) star -= 1;
        if ((before?.attachmentCount ?? 0) > 0) attach -= 1;
        if (before?.myType === 'TO') to -= 1;
      } else {
        if (patch.isRead === true && before?.isRead === false) unread -= 1;
        if (patch.isRead === false && before?.isRead === true) unread += 1;
        if (patch.isStarred === true && before?.isStarred === false) star += 1;
        if (patch.isStarred === false && before?.isStarred === true) star -= 1;
      }
      return {
        ...cur,
        unread: clamp(unread),
        star: clamp(star),
        attach: clamp(attach),
        to: clamp(to),
      };
    });
    // 화면 계산은 어림이다 — 서버가 센 값으로 곧 덮는다. 다만 **바로 묻지 않는다**(아래 참조).
    refetchCountsSoon();
  }, [refetchCountsSoon]);

  /** 서버가 센 **거른 뒤** 총 통수 — 쪽 번호가 쓴다. null 이면 받아 온 것으로 센다. */
  const [listTotal, setListTotal] = useState<number | null>(null);
  const serverNew = counts !== null;
  /** 치워 둔 함에서는 줄 위 아이콘과 별표가 달라진다 (2026-08-26). */
  const rowMode: MailRowMode = folder === 'trash' ? 'trash' : folder === 'spam' ? 'spam' : 'normal';
  const listBodyRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * 환경설정은 **이 셸 안의 컨텐츠**다 (2026-09-05 사용자 지시).
   *
   * ★ 여태 `/mail/settings` 가 제 화면이라 좌측 메일함이 통째로 사라졌다. 라우트는 그대로 두되
   *   그 주소도 이 화면이 받아, **오른쪽 칸만** 목록·상세에서 환경설정으로 바뀐다.
   *   주소가 남으니 링크·새로고침·뒤로가기는 그대로 듣는다.
   */
  const showSettings = location.pathname.startsWith('/mail/settings');
  /**
   * 작성도 **이 셸 안의 컨텐츠**다 (2026-09-08 사용자 지시).
   *
   * ★★★ 여태 `/mail/compose` 가 제 화면이라 **쓰기·답장을 누르면 좌측 메일함이 통째로
   *    사라졌다.** 환경설정과 같은 판단으로 오른쪽 칸만 바뀐다 — 주소는 그대로라
   *    링크 · 새로고침 · 뒤로가기 · 라우터 state(답장 인용) · `?to=me` 가 전부 그대로 듣는다.
   */
  const showCompose = location.pathname.startsWith('/mail/compose');
  /**
   * 메일함을 고르면 **설정·작성에서 나온다** — 안 그러면 고른 메일함이 안 보인다.
   * ★ 작성 중에 나가면 쓰던 것은 사라진다. 종전 「← 목록」 단추도 묻지 않고 버렸으므로
   *   **이 화면의 종전 약속 그대로**다(작성에 «저장 안 됨» 물음은 이 판의 몫이 아니다).
   */
  const leaveSettings = useCallback(() => {
    if (showSettings || showCompose) navigate('/mail');
  }, [showSettings, showCompose, navigate]);
  /**
   * 보낸편지함에서 **치운 메일 보기** (migration 115, 2026-08-23).
   *
   * 보낸 메일을 치우면 보낸편지함에서 빠진다. 되돌리려면 그것을 볼 자리가 있어야 하는데,
   * 휴지통은 **받는 사람 행** 기반이라 보낸 메일이 낄 자리가 없다 — 여기 토글로 본다.
   */
  const [showSentTrashed, setShowSentTrashed] = useState(false);

  /**
   * 쓰기는 **한 길**로 모은다 (2026-08-22) — /mail/compose 화면.
   *
   * 여태 작성·내게쓰기·답장·전달이 전부 가운데 뜨는 팝업이었다. 화면으로 옮기면 뒤로가기가
   * 듣고 링크로 열린다. 쓰는 자리가 둘이면 한쪽만 고쳐지는 날이 오므로 답장·전달도 같이 온다.
   *
   * ★ 미리 채울 값은 **라우터 state** 로 넘긴다 — 인용 본문이 길어 주소에 실을 수 없다.
   */
  const goCompose = useCallback((state?: MailComposeNavState, search?: string) => {
    /*
     * 메일쓰기 방식 (2026-09-05) — 「환경설정 > 기본환경」.
     *
     * ★ 「팝업쓰기」면 화면을 떠나지 않고 창으로 연다. 알맹이는 화면판과 **같은 ComposeModal**
     *   이다 — 보내기·임시저장·수신자 검증을 두 벌로 두면 한쪽만 고쳐지는 날이 온다.
     * ★ 조직도 팝업이 담아 둔 사람은 **여기서 꺼낸다.** 화면으로 갈 때는 그 화면이 꺼내지만,
     *   창으로 열면 갈 곳이 없어 스토어에 영원히 남는다.
     * ★ 기본은 'PAGE' 라, 아무것도 안 고친 사람에게는 지금까지와 똑같다.
     */
    if (mailPrefs.composeWindow === 'POPUP') {
      const pending = consumePendingTo();
      const prefill: ComposePrefill | null = state?.prefill
        ?? (search === '?to=me' && myUser
          ? { to: [{ id: String(myUser.id), name: myUser.name, email: myUser.email, position: null }] }
          : pending
            ? { to: [{ id: pending.id, name: pending.name, email: pending.email, position: pending.position ?? null }] }
            : null);
      setComposePop({ prefill });
      return;
    }
    navigate({ pathname: '/mail/compose', search }, state ? { state } : undefined);
  }, [navigate, mailPrefs.composeWindow, consumePendingTo, myUser]);

  const applyView = useCallback((next: Partial<typeof DEFAULT_MAIL_VIEW>) => {
    setView((prev) => {
      const merged = { ...prev, ...next };
      saveMailView(merged);
      return merged;
    });
  }, []);

  /** 끄는 동안 — 화면만 바꾼다. 기기 저장은 손을 뗄 때 한 번. */
  const previewRatio = useCallback((value: number) => {
    setView((prev) => ({ ...prev, ...(prev.openMode === 'bottom' ? { bottomRatio: value } : { sideRatio: value }) }));
  }, []);

  const commitRatio = useCallback((value: number) => {
    setView((prev) => {
      const merged = { ...prev, ...(prev.openMode === 'bottom' ? { bottomRatio: value } : { sideRatio: value }) };
      saveMailView(merged);
      return merged;
    });
  }, []);

  // 방식을 바꾸면 지금 열려 있는 것도 그 방식으로 다시 선다 — 닫지는 않는다.
  const pickOpenMode = useCallback((mode: MailOpenMode) => {
    applyView({ openMode: mode });
    setOpenMenuOpen(false);
    setDetailState((st) => (st === 'closed' ? 'closed' : (mode === 'full' ? 'full' : 'open')));
  }, [applyView]);

  const openMail = useCallback((id: string) => {
    setSelectedMailId(id);
    setDetailState(view.openMode === 'full' ? 'full' : 'open');
  }, [view.openMode]);

  const closeDetail = useCallback(() => setDetailState('closed'), []);
  /**
   * 메일함을 옮기면 **상세에서 나온다** (2026-09-08 사용자 지적).
   *
   * ★★★ 상세를 «전체»로 열어 둔 채 받은편지함 · 보낸편지함을 누르면, 목록은 바뀌는데
   *    **상세가 그 위를 계속 덮고 있어** 고른 메일함이 안 보였다 — 「눌렀는데 아무 일도
   *    안 일어난다」로 읽힌다.
   * ★★ 고른 것도 함께 지운다 — 다른 함의 메일 id 가 남으면 새 목록에서 **엉뚱한 줄이
   *    골라진 것처럼** 보인다(id 는 함을 가리지 않는다).
   */
  const leaveDetail = useCallback(() => {
    setSelectedMailId(null);
    setDetailState('closed');
  }, []);

  const toggleFull = useCallback(() => {
    setDetailState((st) => (st === 'full'
      ? (view.openMode === 'full' ? 'closed' : 'open')
      : 'full'));
  }, [view.openMode]);

  /**
   * 빠른 조회 — 안읽음 · 중요 · 첨부 · 나에게 (2026-08-21 사용자 지시로 뜻이 바뀌었다).
   *
   * ★ 넷은 **겹쳐 쓰는 거르개가 아니라 하나씩 열어 보는 조회**다. 겹쳐 켜면
   *   "안읽음이면서 첨부가 있는 것" 같은, 아무도 요청하지 않은 교집합이 만들어진다.
   *   그래서 하나를 누르면 **그것만** 켜지고, 켜진 것을 다시 누르면 전체로 돌아온다.
   * ★ 자료 구조(Set)는 그대로 둔다 — 서버에 보내는 질의도, 옛 서버용 화면 거르기도
   *   이미 이 모양을 읽고 있다. 여기서 **원소를 하나로 제한**하는 것으로 충분하다.
   */
  /**
   * ★★★ **빠른 조회도 «목록이 바뀌는 일»이다** (2026-09-08 · 운영 실측).
   *    #1057 은 메일함 넷을 고쳤는데 **이 넷은 빠뜨렸다** — 상세를 「전체보기」로 열어 둔 채
   *    안읽음 · 중요 · 첨부 · 나에게를 누르면 목록은 바뀌는데 **상세가 그 위를 계속 덮어**
   *    사용자에게는 「눌렀는데 아무 일도 안 일어난다」로 읽힌다(사용자가 처음 지적한 것과
   *    **같은 증상**이다 — 「받은편지함, 중요, 보낸편지함 등」의 «등»에 이것이 들어 있었다).
   * ★★ 규칙은 함이냐 거르개냐가 아니라 **「목록이 바뀌면 상세에서 나온다」** 하나다.
   *    자리마다 따로 판단하면 이렇게 한 자리씩 빠진다.
   */
  const toggleStatFilter = useCallback((key: MailStatKey) => {
    leaveSettings();
    leaveDetail();
    setStatFilters((prev) => (prev.has(key) ? new Set<MailStatKey>() : new Set<MailStatKey>([key])));
    setCollapsedGroups(new Set());
    setPage(1);
  }, [leaveSettings, leaveDetail]);

  /** 초기화도 거르개를 걷어 **목록을 바꾼다** — 위와 같은 규칙이다. */
  const resetView = useCallback(() => {
    leaveDetail();
    setStatFilters(new Set());
    applyView({ groupBy: 'none' });
    setCollapsedGroups(new Set());
    setFilterMenuOpen(false);
    setPage(1);
  }, [applyView, leaveDetail]);

  const pickGroupBy = useCallback((by: MailGroupBy) => {
    applyView({ groupBy: by });
    setCollapsedGroups(new Set());
  }, [applyView]);

  /**
   * 목록 밀도 (2026-09-05) — 「환경설정 > 기본환경 > 메일 목록 밀도」와 **한 값**이다.
   *
   * ★ 기기에 먼저 적고(바로 보여야 한다) 서버에도 보낸다. 서버가 진실이고 기기는 사본이다 —
   *   다른 자리에 앉아도 내 목록이 같은 밀도로 서야 한다.
   * ★ 서버 저장이 실패해도 **되돌리지 않는다.** 방금 누른 것이 화면에서 튕겨 나가면
   *   사용자는 무엇이 잘못됐는지 알 수 없다. 이번 화면 동안은 고른 대로 보인다.
   */
  const pickDensity = useCallback((next: MailListDensity) => {
    // ★ 손댔다고 적어 둔다 — 설정이 늦게 도착해 방금 고른 것을 되돌리는 일을 막는다.
    densityTouched.current = true;
    applyView({ density: next });
    void patchMySettings({ mailListDensity: next })
      .then(() => { invalidateMailPrefs(); })
      .catch(() => { /* 옛 서버는 이 칸을 모른다 — 기기 사본만으로 이번 판은 돈다 */ });
  }, [applyView]);

  /** 넓게인가 — 아바타·본문 미리보기를 함께 보이는 상태. */
  const wideDensity = view.density === 'WIDE';

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const drag = useDragSelect(setPicked);

  /**
   * Esc 로 한 단계씩 되돌린다 — full → open → closed.
   *
   * 작성창·팝업이 떠 있으면 손대지 않는다(그쪽이 먼저 받아야 한다). 전체열림은 open 을
   * 거치지 않으므로 한 번에 닫힌다.
   */
  useEffect(() => {
    // 작성은 이제 **다른 화면**이라 여기 떠 있을 수 없다 — 팝업보기만 남는다.
    if (detailState === 'closed' || popout) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setDetailState((st) => (st === 'full' && view.openMode !== 'full' ? 'open' : 'closed'));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detailState, popout, view.openMode]);

  /*
   * 조직도 팝업 → "메일쓰기" 로 넘어온 경우 (2026-08-22 개편).
   *
   * 여태 여기서 꺼내 팝업을 열었다. 이제 쓰기는 화면이라 **작성 화면으로 보내기만** 하고,
   * 꺼내는 일은 그 화면이 한다 — 여기서 꺼내면 화면에 도착하기 전에 스토어가 비어 버린다.
   */
  const pendingTo = useMailComposeStore((s) => s.pendingTo);
  useEffect(() => {
    if (!pendingTo) return;
    goCompose();
  }, [pendingTo, goCompose]);

  /*
   * 보내고 돌아왔다 (2026-08-22) — 작성 화면이 `sentFolder` 를 들려 보낸다.
   *
   * 팝업이던 시절에는 같은 화면 안이라 곧장 폴더를 바꿨다. 화면이 갈렸으니 그 사실을
   * 주소에 실어 온다. ★ 한 번 쓰고 **지운다**(replace) — 안 그러면 새로고침할 때마다
   * 보낸편지함으로 튄다.
   */
  const sentFolder = (location.state as { sentFolder?: MailFolder } | null)?.sentFolder;
  useEffect(() => {
    if (!sentFolder) return;
    setFolder(sentFolder);
    setActiveFolderId(null);
    setPage(1);
    setRefreshKey((k) => k + 1);
    navigate('/mail', { replace: true, state: null });
  }, [sentFolder, navigate]);

  // 첫 화면은 **한 번만** 적용한다 — 값이 늦게 와도 사용자가 이미 고른 폴더를 되돌리면 안 된다.
  useEffect(() => {
    if (!prefsReady || firstFolderApplied) return;
    setFirstFolderApplied(true);
    if (mailPrefs.firstFolder !== 'inbox') setFolder(mailPrefs.firstFolder);
  }, [prefsReady, firstFolderApplied, mailPrefs.firstFolder]);

  /**
   * 밀도는 **서버가 진실이다** (2026-09-05).
   *
   * 기기 사본(mailView)으로 먼저 그리고, 서버 값이 오면 그것으로 맞춘다 — 다른 자리에 앉아
   * 밀도를 바꿨으면 여기서도 그렇게 서야 한다.
   * ★ **한 번만** 한다. 계속 맞추면 방금 이 화면에서 바꾼 것이 되돌아간다.
   * ★ 같은 값이면 아무것도 안 한다 — 뜻 없는 저장으로 다른 탭을 깨우지 않는다.
   */
  const densityTouched = useRef(false);
  const [densitySynced, setDensitySynced] = useState(false);
  useEffect(() => {
    if (!prefsReady || densitySynced) return;
    setDensitySynced(true);
    // 설정이 오기 전에 사용자가 먼저 골랐으면 그것이 이긴다 — 방금 누른 것이 되돌아가지 않는다.
    if (densityTouched.current) return;
    if (mailPrefs.listDensity !== view.density) applyView({ density: mailPrefs.listDensity });
    // view.density 를 의존에 넣지 않는다 — 넣으면 사용자가 바꿀 때마다 이 효과가 다시 돈다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsReady, densitySynced, mailPrefs.listDensity, applyView]);

  /**
   * 셈은 메일함·조건·검색이 바뀔 때마다 한 번. 넷을 한 호출로 받는다.
   * ★ 이 한 번의 물음이 "서버가 새것인가" 의 대답도 겸한다 — 전체메일·내게쓴메일함·거르기·
   *   총 통수가 모두 같은 판에 들어왔기 때문이다.
   */
  useEffect(() => {
    let cancelled = false;
    // 내 메일함(migration 114)은 **어느 메일함인지**까지 줘야 한다 — 안 주면 서버가
    // "폴더 미지정" 을 세어, 내 메일함을 보는데 받은편지함 숫자가 뜬다.
    const scopeId = folder === 'my' && activeFolderId ? activeFolderId : undefined;
    void getMailCounts(folder, scopeId).then((res) => {
      if (cancelled) return;
      setCounts(res);
    });
    return () => { cancelled = true; };
  }, [folder, refreshKey, activeFolderId, countsKey]);

  const reloadFolders = useCallback(() => {
    void listMailFolders().then((res) => {
      setFolders(res);
      // 길이 없는 옛 서버도 빈 배열을 준다 — 그때는 "폴더가 없다" 와 구분되지 않으므로
      // 여기서는 아무 말도 하지 않는다. 실패는 만들기·이름 바꾸기가 알려 준다.
    });
  }, []);

  useEffect(() => { reloadFolders(); }, [reloadFolders, refreshKey]);

  /**
   * 오른쪽 환경설정 「메일함」 탭에서 메일함을 고치면 **레일도 그 자리에서 따라간다**
   * (2026-09-06 사용자 육안 지적 — 만들어도 옆 레일에 안 생겼다).
   * ★ 목록만이 아니라 `refreshKey` 를 올린다 — 「비우기」는 개수도 바꾸기 때문이다.
   * ★ `notifySelf` 는 **내가 고쳤을 때 나에게 되돌아오는 것**을 막는 손잡이다.
   */
  const notifySelf = useRef<() => void>(() => {});
  useEffect(() => {
    const fn = () => setRefreshKey((k) => k + 1);
    notifySelf.current = fn;
    return subscribeMailFolders(fn);
  }, []);

  const activeSort = sortPrefs[folder] ?? DEFAULT_SORT;

  /**
   * 거르기를 **서버에 맡긴다**(서버가 새것일 때). 화면에서 거르면 받아 온 것 중에서만 걸러지므로
   * "안읽음 196" 을 눌렀는데 50통만 나오는 일이 생긴다.
   * ★ 서버가 옛것이면 아무것도 안 보낸다 — 그때는 아래 visibleMessages 가 화면에서 거른다.
   */
  const serverFilterParams = useMemo(() => (serverNew ? {
    ...(statFilters.has('unread') ? { unread: true } : {}),
    ...(statFilters.has('star') ? { star: true } : {}),
    ...(statFilters.has('clip') ? { attach: true } : {}),
    ...(statFilters.has('to') ? { to: true } : {}),
    withTotal: true,
  } : {}), [serverNew, statFilters]);

  const applySort = useCallback((next: SortPref) => {
    setSortPrefs((prev) => {
      const merged = { ...prev, [folder]: next };
      try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(merged)); } catch { /* 기기 저장 실패는 무시 */ }
      return merged;
    });
    setSortOpen(false);
  }, [folder]);

  // 글자마다 부르면 목록이 깜빡인다 — 250ms 쉬면 그때 한 번 부른다.
  useEffect(() => {
    const h = setTimeout(() => setQuery(searchInput.trim()), 250);
    return () => clearTimeout(h);
  }, [searchInput]);

  const togglePick = useCallback((id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const runBulk = useCallback(async (patch: {
    isRead?: boolean; isStarred?: boolean; isTrash?: boolean; isSpam?: boolean; blockSender?: boolean;
  }) => {
    if (bulkBusy || picked.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = [...picked];
      // 서버에 보내기 **전** 상태를 챙겨 둔다 — 응답이 오면 목록은 이미 바뀐 뒤다.
      const pickedBefore = new Map(messagesRef.current.filter((m) => picked.has(m.id)).map((m) => [m.id, m]));
      const res = await bulkPatchMail(ids, patch);
      const changed = new Set(res.ids);
      // 다른 메일함으로 옮겨진 것은 이 목록에서 사라진다 — 지금 보고 있는 함이 그 함이면 남긴다.
      const movesAway =
        (patch.isTrash === true && folder !== 'trash')
        // ★ 2026-08-25 — 휴지통에서 **되돌린** 줄도 이 목록에서 빠진다. 안 빼면 "복원했는데
        //   그대로" 가 되고, 실제로 메뉴를 옮겼다 와야 사라졌다(사용자 지적).
        || (patch.isTrash === false && folder === 'trash')
        || (patch.isSpam === true && folder !== 'spam')
        || (patch.isSpam === false && folder === 'spam');
      setMessages((prev) => (movesAway
        ? prev.filter((m) => !changed.has(m.id))
        : prev.map((m) => (changed.has(m.id) ? { ...m, ...patch } : m))));
      // 요약 숫자 — 옮겨진 줄은 성질만큼 빼고, 남는 줄은 바뀐 값만 반영한다.
      for (const id of changed) {
        const before = pickedBefore.get(id);
        adjustCounts(before, { isRead: patch.isRead, isStarred: patch.isStarred }, movesAway);
      }
      setPicked(new Set());
      setSpamAsk(false);
      setTrashAsk(false);
      // ★ 화면만 고치고 끝내지 않는다 — **서버에 다시 물어** 개수까지 맞춘다(2026-08-25 사용자 지적).
      //   이미 있는 refreshKey 를 쓴다: 목록 effect 와 개수 effect 가 둘 다 이 값을 본다.
      if (movesAway) setRefreshKey((k) => k + 1);
      if (patch.isTrash === false && folder === 'trash') {
        setDoneNotice({
          title: tMail('mail.bulk.restored_title'),
          desc: tMail('mail.bulk.restored_desc').replace('{n}', String(changed.size)),
        });
      }
    } catch {
      // 실패하면 고른 것을 그대로 남긴다 — 다시 누를 수 있어야 한다.
    } finally {
      setBulkBusy(false);
    }
  }, [bulkBusy, folder, picked, adjustCounts]);

  /**
   * 고른 것을 영원히 지운다 (2026-08-25) — 휴지통·스팸함에서만.
   *
   * 서버가 "치워 둔 것인지" 다시 확인하므로, 화면이 잘못 보내도 받은편지함 메일은 안 지워진다.
   * 지운 뒤에는 **목록을 다시 받아온다** — 화면에서만 지우면 개수가 어긋난다.
   */
  const runPurge = useCallback(async (ids: string[]) => {
    if (bulkBusy || ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await purgeMail(ids);
      setPicked(new Set());
      setPurgeAsk(null);
      setRefreshKey((k) => k + 1);
      setDoneNotice({
        title: tMail('mail.bulk.purged_title'),
        desc: tMail('mail.bulk.purged_desc').replace('{n}', String(res.deleted)),
      });
    } catch {
      // 실패하면 고른 것을 그대로 남긴다 — 다시 누를 수 있어야 한다.
      setPurgeAsk(null);
    } finally {
      setBulkBusy(false);
    }
  }, [bulkBusy]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedMailId(null);
    setPicked(new Set());   // 목록이 바뀌면 고른 것도 버린다 — 안 보이는 줄이 골라진 채 남지 않게.
    setHiddenIds(new Set()); // 새로 받아 온 목록에는 감출 줄이 없다.
    setPage(1);             // 메일함·정렬·검색이 바뀌면 첫 쪽으로.
    setSpamAsk(false);
    setTrashAsk(false);
    setNextCursor(null);   // 메일함·정렬·검색이 바뀌면 앞의 커서는 뜻을 잃는다.
    // ★ 기본값(날짜·최신순)이면 정렬을 **아예 보내지 않는다** — 질의가 지금까지와 한 글자도
    //   다르지 않으므로, 정렬을 모르는 서버에서도 그대로 돈다(폴백이 필요 없다).
    const sortParams = activeSort.sort === 'date' && activeSort.order === 'desc'
      ? {}
      : { sort: activeSort.sort, order: activeSort.order };
    listMail(folder, {
      limit: mailPrefs.pageSize, ...sortParams, ...(query ? { q: query } : {}),
      ...serverFilterParams,
      ...(folder === 'my' && activeFolderId ? { folderId: activeFolderId } : {}),
      ...(folder === 'sent' && showSentTrashed ? { trashed: true } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        // 방어: 응답에 messages 가 없거나 배열이 아니면 빈 배열 — render 시 messages.length 크래시 방지.
        setMessages(Array.isArray(res?.messages) ? res.messages : []);
        setNextCursor(res?.nextCursor ?? null);
        setListTotal(typeof res?.total === 'number' ? res.total : null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'mail_fetch_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [folder, refreshKey, activeSort.sort, activeSort.order, mailPrefs.pageSize, query, serverFilterParams, activeFolderId, showSentTrashed]);

  /**
   * 다음 쪽을 이어 붙인다.
   *
   * ★ 이미 있는 id 는 걸러 낸다 — 건너뛰기(offset) 방식이라, 넘기는 사이에 새 메일이 오면
   *   같은 줄이 두 번 올 수 있다. 키가 겹치면 React 가 경고를 내고 화면도 어지러워진다.
   */
  const loadMore = useCallback(async () => {
    if (loadingMore || nextCursor === null) return;
    setLoadingMore(true);
    try {
      const sortParams = activeSort.sort === 'date' && activeSort.order === 'desc'
        ? {}
        : { sort: activeSort.sort, order: activeSort.order };
      const res = await listMail(folder, {
        limit: mailPrefs.pageSize, cursor: nextCursor, ...sortParams, ...(query ? { q: query } : {}),
      });
      const more = Array.isArray(res?.messages) ? res.messages : [];
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...more.filter((m) => !seen.has(m.id))];
      });
      setNextCursor(res?.nextCursor ?? null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'mail_fetch_failed');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, nextCursor, activeSort.sort, activeSort.order, folder, mailPrefs.pageSize, query]);

  // 상세 패널의 별표/휴지통/읽음 PATCH 가 list row 도 동시 갱신.
  const handleLocalUpdate = useCallback(
    (mailId: string, partial: { isStarred?: boolean; isTrash?: boolean; isRead?: boolean; isSpam?: boolean }) => {
      // 요약 숫자도 함께 고친다 — 고치기 **전** 상태를 알아야 얼마를 뺄지 정할 수 있다.
      const before = messagesRef.current.find((m) => m.id === mailId);
      setMessages((prev) =>
        prev.map((m) => (m.id === mailId ? { ...m, ...partial } : m)),
      );
      /*
       * 이 메일함에서 빠져나갔나 — 여러 개 고르기(runBulk)가 쓰는 것과 같은 판정이다.
       * ★ 되돌아오는 경우(되돌리기·PATCH 실패 롤백)도 같은 자리에서 본다 — 감춘 것을 다시 편다.
       *
       * ★ 2026-08-23 — **'치운 메일 보기' 는 판정이 뒤집힌다.** 그 화면은 치운 것**만** 보여
       *   주므로, 되돌린 줄이 사라져야 하고 치운 줄이 남아야 한다. 휴지통과 같은 규칙이다.
       */
      const trashedView = folder === 'trash' || (folder === 'sent' && showSentTrashed);
      const away =
        (partial.isTrash === true && !trashedView)
        || (partial.isTrash === false && trashedView)
        || (partial.isSpam === true && folder !== 'spam')
        || (partial.isSpam === false && folder === 'spam')
        || (partial.isStarred === false && folder === 'star');
      const back =
        (partial.isTrash === false && !trashedView)
        || (partial.isTrash === true && trashedView)
        || (partial.isSpam === false && folder !== 'spam')
        || (partial.isSpam === true && folder === 'spam')
        || (partial.isStarred === true && folder === 'star');
      // 이 메일함에서 빠지면 그 통의 성질만큼 전부, 남으면 바뀐 값만.
      adjustCounts(before, partial, away);
      if (!away && !back) return;
      setHiddenIds((prev) => {
        if (away === prev.has(mailId)) return prev;   // 이미 그 상태면 그대로 둔다
        const next = new Set(prev);
        if (away) next.add(mailId); else next.delete(mailId);
        return next;
      });
    },
    [folder, showSentTrashed, adjustCounts],
  );

  /* ── 요약 · 거르기 · 묶기 ─────────────────────────────────────────────
   * ★ 안읽음·중요는 **지금 불러온 목록** 기준이다. 서버에 세는 길이 없어서(메일 모듈은
   *   목록·작성·상태변경 셋뿐) 화면이 가진 것만 셀 수 있다. 더 받을 게 남아 있으면 `+` 를
   *   붙여 "적어도 이만큼" 이라고 말한다.
   * ★ 첨부·나에게(TO)는 **목록 응답에 칸이 아예 없다**(MailListItem). 자리는 잡아 두고
   *   숫자는 `—`, 누를 수 없게 둔다 — 있는 척하면 눌렀을 때 거짓말이 된다.
   */
  const statCounts: MailStatCounts = useMemo(() => (counts ? {
    // 서버가 센 값 — 메일함 전체 기준이라 `+` 가 필요 없다.
    unread: counts.unread ?? 0,
    star: counts.star ?? 0,
    clip: counts.attach,
    to: counts.to ?? 0,
  } : {
    // 옛 서버 — 받아 온 것만 셀 수 있다. 첨부·나에게는 목록 응답에 칸이 없어 세지도 못한다.
    unread: messages.filter((m) => !m.isRead).length,
    star: messages.filter((m) => m.isStarred).length,
    clip: null,
    to: null,
  }), [counts, messages]);

  const visibleMessages = useMemo(() => {
    // 상세에서 치운 줄은 이 메일함에서 뺀다(감추기만 한다 — 되돌아오면 다시 보인다).
    const kept = hiddenIds.size === 0 ? messages : messages.filter((m) => !hiddenIds.has(m.id));
    // 서버가 이미 걸렀으면 또 거르지 않는다 — 두 번 거르면 중요(별표)처럼 서버에 없는 조건이
    // 목록을 텅 비운다.
    if (serverNew || statFilters.size === 0) return kept;
    return kept.filter((m) => {
      if (statFilters.has('unread') && m.isRead) return false;
      if (statFilters.has('star') && !m.isStarred) return false;
      return true;
    });
  }, [messages, statFilters, serverNew, hiddenIds]);

  /** 지금 쪽에 보일 것만 자른다. 묶기도 이 쪽 안에서 돈다. */
  const pagedMessages = useMemo(
    () => visibleMessages.slice((page - 1) * view.pageSize, page * view.pageSize),
    [visibleMessages, page, view.pageSize],
  );

  const groups = useMemo(
    () => groupMail(pagedMessages, view.groupBy),
    [pagedMessages, view.groupBy],
  );

  /** 쪽이 목록 밖으로 나가면 끌어당긴다(걸러 내거나 지워서 줄어든 경우). */
  useEffect(() => {
    if (loadingMore) return;
    const pages = Math.max(1, Math.ceil(visibleMessages.length / view.pageSize));
    if (page > pages) setPage(pages);
  }, [visibleMessages.length, view.pageSize, page, loadingMore]);

  /**
   * 쪽 넘김. 받아 온 것보다 뒤로 가려 하면 **그때 더 받아 온다** —
   * 사용자에겐 쪽 넘김 하나로 보인다.
   */
  const goToPage = useCallback(async (next: number) => {
    if (next < 1) return;
    // 총 통수를 알아도 **받아 온 것**보다 뒤라면 더 받아 와야 한다.
    if (next * view.pageSize > visibleMessages.length && nextCursor !== null) {
      await loadMore();
    }
    setPage(next);
    if (listBodyRef.current) listBodyRef.current.scrollTop = 0;
  }, [view.pageSize, visibleMessages.length, nextCursor, loadMore]);

  /** 지금 쪽 전체 고르기 — 메일함 전체가 아니다(서버가 폴더 단위로 받아 주지 않는다). */
  const pagedIds = useMemo(() => pagedMessages.map((m) => m.id), [pagedMessages]);
  const allPickedOnPage = pagedIds.length > 0 && pagedIds.every((id) => picked.has(id));
  const somePickedOnPage = pagedIds.some((id) => picked.has(id));

  const toggleAllOnPage = useCallback(() => {
    setPicked((prev) => {
      const next = new Set(prev);
      const all = pagedIds.length > 0 && pagedIds.every((id) => next.has(id));
      pagedIds.forEach((id) => { if (all) next.delete(id); else next.add(id); });
      return next;
    });
  }, [pagedIds]);

  /* ── 줄 위 빠른 작업 ─────────────────────────────────────────────────── */
  const handleRowReply = useCallback((m: MailListItem) => {
    goCompose({
      badge: 'reply',
      prefill: {
        to: [{ id: m.sender.id, name: m.sender.name, email: m.sender.email, position: null }],
        subject: m.subject.startsWith('Re: ') ? m.subject : `Re: ${m.subject}`,
      },
    });
  }, [goCompose]);

  const handleRowPopout = useCallback((m: MailListItem) => {
    setPopout({ id: m.id, title: m.subject || tMail('mail.item.subject_empty') });
  }, []);

  const handleRowTrash = useCallback(async (m: MailListItem) => {
    if (rowBusy) return;
    setRowBusy(m.id);
    try {
      // 치운 메일을 보고 있으면 같은 버튼이 **되돌리기**다 (migration 115).
      await patchMail(m.id, { isTrash: !(folder === 'sent' && showSentTrashed) });
      // 휴지통에서 보고 있는 게 아니면 그 줄은 이 목록에서 사라진다.
      setMessages((prev) => (folder === 'trash'
        ? prev.map((x) => (x.id === m.id ? { ...x, isTrash: true } : x))
        : prev.filter((x) => x.id !== m.id)));
      // 이 목록에서 빠지면 그 통의 성질만큼 요약 숫자도 줄인다.
      adjustCounts(m, {}, folder !== 'trash');
      setPicked((prev) => {
        if (!prev.has(m.id)) return prev;
        const next = new Set(prev);
        next.delete(m.id);
        return next;
      });
      if (selectedMailId === m.id) {
        setSelectedMailId(null);
        setDetailState('closed');
      }
    } catch {
      // 실패하면 줄을 그대로 둔다 — 다시 누를 수 있어야 한다.
    } finally {
      setRowBusy(null);
    }
  }, [rowBusy, folder, selectedMailId, showSentTrashed, adjustCounts]);

  /**
   * 줄의 별표 토글 (2026-08-21).
   *
   * ★ 여태 목록의 별은 **장식용 그림**이라 눌러도 별이 아니라 줄이 눌렸다 — 그래서
   *   "즐겨찾기를 누르면 상세보기로 넘어간다" 가 됐다. 이제 진짜 버튼이고 여기가 그 손이다.
   * ★ 중요 메일함에서 별을 풀면 그 줄은 이 목록에서 빠진다 — 안 빠지면 "풀었는데 그대로" 다.
   */
  /**
   * 줄 되돌리기 (2026-08-26) — 휴지통이면 원래 자리로, 스팸이면 스팸 아님으로.
   *
   * 되돌린 줄은 **이 목록에서 빠진다**(치워 둔 함을 보고 있으므로). 화면에서만 지우지 않고
   * `refreshKey` 를 올려 서버에 다시 묻는다 — 개수도 같이 맞는다(일괄 처리와 같은 규약).
   */
  const handleRowRestore = useCallback(async (m: MailListItem) => {
    if (rowBusy) return;
    setRowBusy(m.id);
    try {
      await patchMail(m.id, folder === 'spam' ? { isSpam: false } : { isTrash: false });
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      adjustCounts(m, {}, true);
      setPicked((prev) => {
        if (!prev.has(m.id)) return prev;
        const next = new Set(prev);
        next.delete(m.id);
        return next;
      });
      if (selectedMailId === m.id) { setSelectedMailId(null); setDetailState('closed'); }
      setRefreshKey((k) => k + 1);
    } catch {
      // 실패하면 줄을 그대로 둔다 — 다시 누를 수 있어야 한다.
    } finally {
      setRowBusy(null);
    }
  }, [rowBusy, folder, selectedMailId]);

  const handleRowToggleStar = useCallback(async (m: MailListItem) => {
    if (rowBusy) return;
    const next = !m.isStarred;
    setRowBusy(m.id);
    // 먼저 화면을 바꾸고 실패하면 되돌린다 — 별표는 기다릴 일이 아니다.
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isStarred: next } : x)));
    // 중요 메일함에서 별을 풀면 그 줄이 빠지므로 성질만큼 전부 줄인다.
    adjustCounts(m, { isStarred: next }, !next && folder === 'star');
    try {
      const res = await patchMail(m.id, { isStarred: next });
      // 보낸 메일에는 myStatus 가 없다 — 별표를 누를 일도 없지만, 오면 화면 값을 지킨다.
      const on = res.myStatus?.isStarred ?? next;
      setMessages((prev) => (!on && folder === 'star'
        ? prev.filter((x) => x.id !== m.id)
        : prev.map((x) => (x.id === m.id ? { ...x, isStarred: on } : x))));
    } catch {
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isStarred: !next } : x)));
      // 되돌렸으니 셈도 되돌린다 — 서버 값이 곧 덮지만 그 사이 숫자가 틀려 보이지 않게.
      adjustCounts({ ...m, isStarred: next }, { isStarred: !next });
    } finally {
      setRowBusy(null);
    }
  }, [rowBusy, folder, adjustCounts]);

  const handleRowToggleRead = useCallback(async (m: MailListItem) => {
    if (rowBusy) return;
    const next = !m.isRead;
    setRowBusy(m.id);
    // 먼저 화면을 바꾸고 실패하면 되돌린다 — 읽음 표시는 기다릴 일이 아니다.
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isRead: next } : x)));
    adjustCounts(m, { isRead: next });
    try {
      await patchMail(m.id, { isRead: next });
    } catch {
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, isRead: !next } : x)));
      adjustCounts({ ...m, isRead: next }, { isRead: !next });
    } finally {
      setRowBusy(null);
    }
  }, [rowBusy, adjustCounts]);

  /* ── 내가 만드는 메일함 (migration 114) ─────────────────────────────── */
  const pickUserFolder = useCallback((node: MailFolderNode) => {
    leaveSettings();
    leaveDetail();
    setFolder('my');
    setActiveFolderId(node.id);
    setStatFilters(new Set());   // 빠른 조회는 메일함마다 새로 시작한다
    setPage(1);
  }, [leaveSettings, leaveDetail]);

  /** 서버가 준 이유를 사람 말로 — 같은 이름(409)만 따로 알려 주고 나머지는 한 문장이다. */
  const reportFolderError = useCallback((err: unknown) => {
    const status = (err as { response?: { status?: number } }).response?.status;
    setFolderMsg(status === 409 ? 'mail.folder.err_name_taken' : 'mail.folder.err_save');
  }, []);

  const handleCreateFolder = useCallback((name: string, parentId: string | null) => {
    setFolderMsg(null);
    void createMailFolder(name, parentId)
      .then(() => { reloadFolders(); notifyMailFoldersChanged(notifySelf.current); })
      .catch(reportFolderError);
  }, [reloadFolders, reportFolderError]);

  const handleRenameFolder = useCallback((id: string, name: string) => {
    setFolderMsg(null);
    void updateMailFolder(id, { name })
      .then(() => { reloadFolders(); notifyMailFoldersChanged(notifySelf.current); })
      .catch(reportFolderError);
  }, [reloadFolders, reportFolderError]);

  const runMarkAllRead = useCallback((target: MailFolder, folderId?: string) => {
    void markFolderRead(target, folderId).then(() => {
      setRefreshKey((k) => k + 1);
    }).catch(() => { /* 구 서버면 길이 없다 — 조용히 넘어간다 */ });
  }, []);

  const handleFolderAction = useCallback((action: FolderMenuAction) => {
    const { kind, folder: node } = action;
    if (kind === 'read-all') { runMarkAllRead('my', node.id); return; }
    if (kind === 'empty') {
      setConfirm({ kind: 'empty', folder: 'my', folderId: node.id, name: node.name, permanent: false });
      return;
    }
    if (kind === 'delete') setConfirm({ kind: 'delete', folderId: node.id, name: node.name });
  }, [runMarkAllRead]);

  const runConfirm = useCallback(async () => {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    try {
      if (confirm.kind === 'empty') {
        await emptyFolder(confirm.folder, confirm.folderId);
      } else {
        await deleteMailFolder(confirm.folderId);
        if (activeFolderId === confirm.folderId) { setActiveFolderId(null); setFolder('inbox'); }
      }
      setConfirm(null);
      reloadFolders();
      setRefreshKey((k) => k + 1);
      notifyMailFoldersChanged(notifySelf.current);
    } catch (err) {
      // 실패하면 창을 닫지 않는다 — 다시 누를 수 있어야 한다.
      reportFolderError(err);
    } finally {
      setConfirmBusy(false);
    }
  }, [confirm, confirmBusy, activeFolderId, reloadFolders, reportFolderError]);

  /*
   * 이동 창에서 고른 메일이 **모두 한 사람**이 보낸 것이면 그 사람 (2026-11-02 사용자 지시 —
   * 「동일한 이메일은 일괄이동」). 섞여 있으면 null — 누구 것을 옮길지 정할 수 없다.
   * ★ 보낸 쪽 메일함(보낸·임시·전체)에는 내 수신자 줄이 없어 서버가 받지 않는다 — 감춘다.
   */
  const moveSender = useMemo(() => {
    if (!movePicker || folder === 'sent' || folder === 'draft' || folder === 'all') return null;
    const picks = messages.filter((m) => movePicker.ids.includes(m.id));
    const email = picks[0]?.sender?.email?.trim().toLowerCase();
    if (!email || picks.length !== movePicker.ids.length) return null;
    return picks.every((m) => m.sender?.email?.trim().toLowerCase() === email) ? picks[0]!.sender : null;
  }, [movePicker, messages, folder]);

  /** 메일을 메일함으로 옮긴다. `null` 이면 받은편지함으로 되돌린다. */
  const runMove = useCallback(async (ids: string[], folderId: string | null) => {
    if (ids.length === 0) return;
    setMovePicker(null);
    try {
      await bulkPatchMail(ids, { folderId });
      // 지금 보고 있는 자리에서 빠져나간 것은 목록에서 지운다.
      setMessages((prev) => prev.filter((m) => !ids.includes(m.id)));
      setPicked(new Set());
      reloadFolders();
    } catch {
      // 실패하면 줄을 그대로 둔다.
    }
  }, [reloadFolders]);

  /** 같은 보낸 사람의 메일을 서버가 모아 옮긴다 — 다른 쪽에 있는 것까지. 끝나면 목록을 다시 읽는다. */
  const runMoveBySender = useCallback(async (email: string, targetFolderId: string | null) => {
    setMovePicker(null);
    try {
      const { moved } = await moveMailBySender(
        folder, folder === 'my' && activeFolderId ? activeFolderId : undefined, email, targetFolderId,
      );
      toast.success(tMail('mail.toast.moved').replace('{n}', String(moved)));
      setPicked(new Set());
      setRefreshKey((k) => k + 1);
      reloadFolders();
    } catch {
      toast.error(tMail('mail.folder.move_failed'));
    }
  }, [folder, activeFolderId, reloadFolders]);
  const doMove = (targetFolderId: string | null) => {
    if (!movePicker) return;
    if (moveBySender && moveSender) { void runMoveBySender(moveSender.email, targetFolderId); return; }
    void runMove(movePicker.ids, targetFolderId);
  };

  /**
   * 내게 쓰기 — 받는사람이 나로 채워진 작성 화면일 뿐, 별도 화면이 아니다.
   * ★ 받는사람을 state 가 아니라 **주소(`?to=me`)** 로 적는다 — 새로고침해도 살아남아야 한다.
   */
  const handleSelfCompose = useCallback(() => {
    // ★ 내가 누구인지 여기서 따지지 않는다 — 그건 작성 화면이 안다. 여기서 걸러 버리면
    //   사용자 정보가 늦게 오는 순간에 '내게 쓰기' 가 그냥 '작성' 이 된다.
    goCompose(undefined, '?to=me');
  }, [goCompose]);

  /** 지금 켜 둔 빠른 조회 — 하나뿐이다(겹쳐 켜지 않는다). */
  const activeStatKey = statFilters.size === 1 ? [...statFilters][0]! : null;

  /**
   * 제목 옆 건수 (2026-08-23 사용자 지적 — "건수가 틀리다").
   *
   * 여태 `messages.length` 즉 **지금까지 받아 온 통수**를 적었다. 첫 쪽만 받은 상태라
   * 219통짜리 받은편지함에 "50건" 이라고 썼고, 바로 아래 쪽 번호는 "1–25 / 219" 라고 해서
   * 한 화면에서 두 숫자가 어긋났다.
   *
   * 서버는 이미 총 통수를 준다(`withTotal: true` — 쪽 번호가 그것을 쓰고 있었다). 제목도
   * 같은 값을 쓴다. 상세에서 방금 치운 줄은 빼 준다 — 안 그러면 치웠는데 숫자가 그대로다.
   * ★ 옛 서버는 총 통수를 안 준다 → 받아 온 만큼을 적고 **더 있으면 `+`** 를 붙인다.
   *   "적어도 이만큼" 이라고 말하는 것이지, 50을 219인 척하지 않는다.
   */
  // 2026-10-14 — 화면 머리의 건수 표시를 지웠다(사용자 지시). 그 숫자만 쓰던 계산(hiddenHere·headerCount·headerPartial)도 함께 뺐다.

  const folderTitle = tMail(FOLDER_TITLE_KEY[folder]);
  const composeLabel = tMail('mail.btn.compose');
  const countSuffix = tMail('mail.list.count_suffix');

  return (
    <div
      className="mail-page"
      data-open={view.openMode}
      data-state={detailState}
      /* 밀도 — 아바타를 되살리는 CSS 가 이 값을 본다(2026-09-05). */
      data-density={view.density}
      data-resizing={resizing ? 'true' : undefined}
      style={{
        '--mail-side-pct': `${(view.sideRatio * 100).toFixed(3)}%`,
        '--mail-bottom-pct': `${(view.bottomRatio * 100).toFixed(3)}%`,
      } as React.CSSProperties}
    >
      <aside className="mail-sidebar" aria-label={tMail('mail.sidebar.aria')}>
        {/*
          ★ 레일 제목 (2026-09-08 사용자 지적) — 근태 · 결재 · 인사 레일에는 다 있는데
            메일만 없어서, 쓰기 단추부터 시작했다. 「여기가 어디인가」가 레일에도 있어야 한다.
        */}
        <h2 className="mail-sidebar-title">{tMail('mail.head.crumb')}</h2>
        {/* 쓰기 두 칸 — 내게 쓰기는 받는사람이 나로 채워진 작성창일 뿐이다. */}
        <div className="mail-write-row">
          <button
            type="button"
            className="mail-sidebar-compose-button"
            aria-current={showCompose ? 'page' : undefined}
            onClick={() => goCompose()}
            aria-label={composeLabel}
          >
            <Edit size={16} /> {composeLabel}
          </button>
          <button
            type="button"
            className="mail-sidebar-compose-button mail-sidebar-compose-button--self"
            onClick={handleSelfCompose}
            aria-label={tMail('mail.btn.self_compose')}
          >
            <User size={15} /> {tMail('mail.btn.self_compose')}
          </button>
        </div>

        <MailStatRow
          counts={statCounts}
          active={statFilters}
          /* ★ `+` 는 "받아 온 것만 셌다" 는 표시다 — 서버가 세 줬으면 그 숫자가 전부라 붙이면 거짓말이다. */
          partial={!serverNew && nextCursor !== null}
          onToggle={toggleStatFilter}
        />
        <ul className="mail-sidebar-folders" role="list">
          {FOLDERS.filter((f) => serverNew || !NEW_SERVER_FOLDERS.includes(f.id)).map((f) => {
            const Icon = f.icon;
            const active = folder === f.id;
            // 비울 수 있는 메일함만 관리 메뉴를 단다 — 보낸메일함은 남의 편지함에도 있는 메일이다.
            const manageable = MANAGEABLE_FOLDERS.includes(f.id);
            return (
              <li key={f.id} role="listitem" className="mail-sidebar-folder-row">
                <button
                  type="button"
                  className="mail-sidebar-folder"
                  aria-pressed={active && !showCompose}
                  onClick={() => {
                    leaveSettings();
                    leaveDetail();
                    setFolder(f.id);
                    setActiveFolderId(null);
                    // 치운 메일 보기는 보낸편지함 안에서만 뜻이 있다 — 나가면 끈다.
                    setShowSentTrashed(false);
                    // 빠른 조회는 메일함마다 새로 시작한다 — 보낸편지함에는 '안읽음' 이 뜻이 없다.
                    setStatFilters(new Set());
                    setPage(1);
                  }}
                >
                  <Icon size={15} aria-hidden />
                  <span className="mail-sidebar-folder-label">{tMail(f.labelKey)}</span>
                </button>
                {/* ★ 버튼 안에 버튼을 넣으면 파서가 바깥을 먼저 닫는다 — **형제**로 세운다. */}
                {manageable && (
                  <button
                    type="button"
                    className="mail-sidebar-folder-tool"
                    aria-label={`${tMail(f.labelKey)} ${tMail('mail.folder.manage')}`}
                    aria-haspopup="menu"
                    onClick={() => setBasicMenu((v) => (v === f.id ? null : f.id))}
                  >
                    <MoreVertical size={15} aria-hidden />
                  </button>
                )}
                {basicMenu === f.id && (
                  <div className="mail-folder-menu" role="menu">
                    <p className="mail-folder-menu-h">{tMail(f.labelKey)}</p>
                    <button type="button" role="menuitem" onClick={() => { setBasicMenu(null); runMarkAllRead(f.id); }}>
                      <Check size={14} aria-hidden />{tMail('mail.folder.read_all')}
                    </button>
                    <div className="mail-folder-menu-sep" />
                    <button
                      type="button"
                      role="menuitem"
                      className="is-danger"
                      onClick={() => {
                        setBasicMenu(null);
                        setConfirm({
                          kind: 'empty', folder: f.id, name: tMail(f.labelKey),
                          permanent: f.id === 'trash' || f.id === 'spam',
                        });
                      }}
                    >
                      <Trash2 size={14} aria-hidden />{tMail('mail.folder.empty')}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {/* 내가 만드는 메일함 — 서버가 새것일 때만 (구 서버는 길이 없어 빈 목록이 온다) */}
        {serverNew && (
          <>
            <MailFolderTree
              folders={folders}
              activeId={folder === 'my' ? activeFolderId : null}
              onPick={pickUserFolder}
              onCreate={handleCreateFolder}
              onRename={handleRenameFolder}
              onAction={handleFolderAction}
            />
            {folderMsg && <p className="mail-folder-msg" role="alert">{tMail(folderMsg)}</p>}
          </>
        )}

        {/*
          환경설정 (2026-09-05 사용자 지시) — **내 저장용량 바로 위**다.
          ★ 메일함 목록에 끼우지 않는다. 메일함은 「메일이 담긴 곳」이고 이것은 「고치는 곳」이라,
            같은 줄에 세우면 눌러서 빈 편지함이 나오는 줄로 읽힌다.
        */}
        <button
          type="button"
          className={`mail-sidebar-settings${showSettings ? ' is-active' : ''}`}
          aria-pressed={showSettings}
          onClick={() => navigate('/mail/settings')}
        >
          <Settings size={15} aria-hidden />
          <span>{tMail('mail.nav.settings')}</span>
        </button>

        <MailQuotaBar />
      </aside>

      {/*
        오른쪽 칸 — **머리와 판을 세로로 쌓는다** (2026-09-08 사용자 지적).
        ★★★ 화면 머리는 **판(카드) 밖**이다. 판 안에 들어가는 것은 «다루는 것» —
           검색 · 거르개 · 목록 · 쪽 넘김까지다. 머리는 「여기가 어디인가」라서
           판에 담기면 «내용의 일부»로 읽힌다(근태 · 인사 · 결재도 판 밖에 있다).
      */}
      <div className="mail-right">
      {/*
        ★★★ 화면 머리는 **정본 `PageHeader`(A3)** 다 (2026-09-08 사용자 지적).
           종전에는 목록 «판 안»에 손으로 짠 `<h2>` 한 줄이라, 근태 · 인사 · 결재에는 있는
           **경로 · 한 줄 설명 · 도움말**이 통째로 없었다.
        ★★ 경로의 **마지막이 지금 화면**이다(A3) — 「메일 › 받은편지함」.
        ★ 작성은 제 도구줄(보내기 · 임시 저장)을 머리 삼는다 — 그때는 이 머리를 세우지 않는다.
      */}
      {showSettings && (
      <PageHeader
        className="mail-head"
        breadcrumb={[tMail('mail.head.crumb'), tMail('mail.settings.title')]}
        title={tMail('mail.settings.title')}
        description={tMail('mail.settings.desc')}
        actions={(
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<ArrowLeft size={14} aria-hidden />}
            onClick={() => navigate('/mail')}
          >
            {tMail('mail.settings.back')}
          </Button>
        )}
        help={{
          title: tMail('mail.head.help_title'),
          body: (
            <>
              <p>{tMail('mail.settings.help_tab')}</p>
              <p>{tMail('mail.settings.help_scope')}</p>
            </>
          ),
        }}
      />
      )}

      {!showSettings && !showCompose && (
      <PageHeader
        className="mail-head"
        breadcrumb={[tMail('mail.head.crumb'), folderTitle]}
        title={folderTitle}
        description={tMail(FOLDER_DESC_KEY[folder] ?? 'mail.head.desc_default')}
        actions={(
          <>
            {/* 2026-10-14 사용자 지시 — 화면 머리 오른쪽의 건수(「490건」)를 지웠다. 모든 메일함이 이 머리를 쓴다. */}
            {/*
              빠른 조회를 켠 동안에는 **무엇만 보고 있는지** 머리에 적는다 —
              목록이 짧아진 이유가 화면 어딘가에 적혀 있어야 "메일이 사라졌다" 가 안 된다.
              누르면 전체로 돌아온다(요약 4칸을 다시 찾아갈 필요가 없다).
            */}
            {activeStatKey && (
              <button
                type="button"
                className="mail-list-viewing"
                title={tMail('mail.stat.viewing_off')}
                onClick={() => toggleStatFilter(activeStatKey)}
              >
                {tMail('mail.stat.viewing').replace('{k}', tMail(STAT_LABEL_KEY[activeStatKey]))}
                <X size={12} aria-hidden />
              </button>
            )}
          </>
        )}
        help={{
          title: tMail('mail.head.help_title'),
          body: (
            <>
              <p>{tMail('mail.head.help_stat')}</p>
              <p>{tMail('mail.head.help_group')}</p>
              <p>{tMail('mail.head.help_trash')}</p>
            </>
          ),
        }}
      />
      )}

      <div className="mail-main">
      {showSettings ? <MailSettingsPage inShell /> : showCompose ? <MailComposePage /> : <>
      <section
        className="mail-list-panel"
        aria-label={`${folderTitle} ${tMail('mail.list.aria_panel_suffix')}`}
        data-dragging={drag.dragging ? 'true' : undefined}
      >

        <div className="mail-search">
          {/* 밑줄 한 줄이 아니라 **상자**로 그린다 — 어디를 누르면 되는지 눈에 보여야 한다. */}
          <div className="mail-search-box">
            <Search size={14} aria-hidden />
            <input
              className="mail-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={tMail('mail.search.placeholder')}
              aria-label={tMail('mail.search.placeholder')}
            />
            {searchInput && (
              <button
                type="button"
                className="mail-search-clear"
                onClick={() => setSearchInput('')}
                aria-label={tMail('mail.search.clear')}
              >
                ×
              </button>
            )}
          </div>
          {/* 묶기·열림·정렬은 **검색과 한 줄**에 둔다(2026-08-21 사용자 지시).
              셋 다 "이 목록을 어떻게 볼까" 라 검색과 같은 층에 있는 것이 맞고,
              제목 줄은 어느 메일함인지만 말한다. */}
          {/* ★ 거르는 일은 **좌측 요약 4칸 한 곳**이다 — 여기는 묶는 일만 한다. */}
          <MailFilterMenu
            open={filterMenuOpen}
            onToggleOpen={() => { setFilterMenuOpen((v) => !v); setOpenMenuOpen(false); }}
            onClose={() => setFilterMenuOpen(false)}
            active={statFilters}
            groupBy={view.groupBy}
            onReset={resetView}
            onGroupBy={pickGroupBy}
            density={view.density}
            onDensity={pickDensity}
          />
          <MailOpenMenu
            open={openMenuOpen}
            onToggleOpen={() => { setOpenMenuOpen((v) => !v); setFilterMenuOpen(false); }}
            onClose={() => setOpenMenuOpen(false)}
            mode={view.openMode}
            onPick={pickOpenMode}
          />
          <div className="mail-sort">
            <button
              type="button"
              className="mail-sort-btn"
              aria-haspopup="menu"
              aria-expanded={sortOpen}
              aria-label={tMail('mail.sort.aria')}
              onClick={() => setSortOpen((v) => !v)}
            >
              <ArrowUpDown size={13} aria-hidden />
              <span>{tMail(SORT_FIELDS.find((f) => f.id === activeSort.sort)?.key ?? 'mail.sort.date')}</span>
              <span className="mail-sort-dir">
                {activeSort.sort === 'date'
                  ? tMail(activeSort.order === 'desc' ? 'mail.sort.newest' : 'mail.sort.oldest')
                  : tMail(activeSort.order === 'asc' ? 'mail.sort.asc' : 'mail.sort.desc')}
              </span>
            </button>
            {sortOpen && (
              <div className="mail-sort-menu" role="menu">
                <p className="mail-sort-heading">{tMail('mail.sort.field_heading')}</p>
                {SORT_FIELDS
                  .filter((f) => !(SENDER_SIDE_FOLDERS.includes(folder) && (f.id === 'sender' || f.id === 'unread')))
                  .map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={activeSort.sort === f.id}
                      className={`mail-sort-opt${activeSort.sort === f.id ? ' is-on' : ''}`}
                      onClick={() => applySort({ sort: f.id, order: f.id === 'unread' ? 'asc' : activeSort.order })}
                    >
                      {tMail(f.key)}
                    </button>
                  ))}
                <p className="mail-sort-heading">{tMail('mail.sort.dir_heading')}</p>
                {(['desc', 'asc'] as MailOrder[]).map((o) => (
                  <button
                    key={o}
                    type="button"
                    role="menuitemradio"
                    aria-checked={activeSort.order === o}
                    className={`mail-sort-opt${activeSort.order === o ? ' is-on' : ''}`}
                    onClick={() => applySort({ sort: activeSort.sort, order: o })}
                  >
                    {activeSort.sort === 'date'
                      ? tMail(o === 'desc' ? 'mail.sort.newest' : 'mail.sort.oldest')
                      : tMail(o === 'asc' ? 'mail.sort.asc' : 'mail.sort.desc')}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {folder === 'spam' && !loading && messages.length > 0 && (
          <p className="mail-spam-banner">{tMail('mail.spam.banner')}</p>
        )}

        {/*
          고른 것이 있으면 **목록 표의 머리줄(칸 이름)** 자리에 선택바가 들어간다(사용자 지시).
          제목 줄은 그대로 둔다 — 어느 메일함을 보고 있는지는 고르는 동안에도 알아야 한다.
          ★ 칸 이름 줄은 넓을 때만 나오지만, 선택바는 **폭과 무관하게** 나온다.
            좁은 목록에서 골랐는데 할 일이 아무 데도 없으면 안 된다.
        */}
        {picked.size > 0 ? (
          <MailSelectionBar
            count={picked.size}
            busy={bulkBusy}
            inSpam={folder === 'spam'}
            /* 휴지통에서는 복원·영구삭제 둘뿐이다 — 읽음·중요·스팸은 버린 편지에 뜻이 없다. */
            inTrash={folder === 'trash'}
            /* 옛 서버에는 고른 것만 지우는 길이 없다 — 그때는 단추를 아예 안 그린다. */
            onPurge={serverNew && folder === 'trash' ? () => setPurgeAsk([...picked]) : undefined}
            onClear={() => setPicked(new Set())}
            onRead={() => { void runBulk({ isRead: true }); }}
            onStar={() => { void runBulk({ isStarred: true }); }}
            onSpam={() => setSpamAsk(true)}
            onNotSpam={() => { void runBulk({ isSpam: false }); }}
            /* 휴지통에서 고른 것은 **되돌리는** 것이라 묻지 않는다 — 되돌릴 수 있는 일에
               확인창은 방해다(상세와 같은 규칙). */
            onTrash={() => {
              if (folder === 'trash') { void runBulk({ isTrash: false }); return; }
              setTrashAsk(true);
            }}
            onMove={serverNew ? () => setMovePicker({ ids: [...picked] }) : undefined}
          />
        ) : (
          <MailListHead
            count={pagedMessages.length}
            allPicked={allPickedOnPage}
            somePicked={somePickedOnPage}
            onToggleAll={toggleAllOnPage}
          />
        )}

        <div className="mail-list-body" ref={listBodyRef}>
          {/*
            치운 메일 보기 (migration 115, 2026-08-23) — **보낸편지함에만** 있다.
            보낸 메일을 치우면 여기서 빠지는데, 휴지통은 받는 사람 행 기반이라 보낸 메일이 낄
            자리가 없다. 되돌릴 자리를 이 토글로 준다.
          */}
          {folder === 'sent' && (
            <button
              type="button"
              className={`mail-sent-trashed${showSentTrashed ? ' is-on' : ''}`}
              aria-pressed={showSentTrashed}
              onClick={() => { setShowSentTrashed((v) => !v); setPage(1); }}
            >
              <Trash2 size={13} aria-hidden />
              {showSentTrashed ? tMail('mail.sent.back_to_live') : tMail('mail.sent.show_trashed')}
            </button>
          )}
          {loading && messages.length === 0 && <div className="mail-list-loading">{tMail('mail.list.loading')}</div>}
          {error && messages.length === 0 && <div className="mail-list-error">{tMail('mail.list.err_prefix')}: {error}</div>}
          {!loading && !error && messages.length === 0 && query && (
            <p className="mail-list-empty">{tMail('mail.search.empty')}</p>
          )}
          {!loading && !error && messages.length === 0 && !query && (
            <div className="mail-list-empty">
              <EmptyState icon={Inbox} title={tMail('mail.list.empty_title')} description={tMail('mail.list.empty_desc')} />
            </div>
          )}
          {/* 걸러 낸 결과가 없을 때 — "메일이 없습니다" 로 오인하지 않게 따로 말한다. */}
          {!loading && !error && messages.length > 0 && visibleMessages.length === 0 && (
            <p className="mail-list-empty">{tMail('mail.search.empty')}</p>
          )}

          {view.groupBy === 'none'
            ? pagedMessages.map((m) => (
              <MailItem
                key={m.id}
                message={m}
                active={m.id === selectedMailId}
                onSearchPerson={searchByPerson}
                onAddContact={(email, name) => setContactAdd({ email, name })}
                onComposeTo={(to) => goCompose({ prefill: { to: [externalChip(to)] } })}
                onOpenContact={() => navigate('/contacts')}
                onSelect={() => { if (!drag.shouldSwallowClick()) openMail(m.id); }}
                simplify={mailPrefs.simplifySubject}
                preview={wideDensity}
                checked={picked.has(m.id)}
                onToggleCheck={() => togglePick(m.id)}
                onCheckPointerDown={drag.onCheckPointerDown}
                rowBusy={rowBusy === m.id}
                onReply={handleRowReply}
                onPopout={handleRowPopout}
                onTrash={(x) => { void handleRowTrash(x); }}
                onToggleRead={(x) => { void handleRowToggleRead(x); }}
                onToggleStar={(x) => { void handleRowToggleStar(x); }}
                onMove={serverNew ? (x) => setMovePicker({ ids: [x.id] }) : undefined}
                mode={rowMode}
                onRestore={(x) => { void handleRowRestore(x); }}
                onPurge={serverNew ? (x) => setPurgeAsk([x.id]) : undefined}
              />
            ))
            : groups.map((g) => {
              const off = collapsedGroups.has(g.key);
              const name = g.bucket ? tMail(BUCKET_LABEL_KEY[g.bucket]) : g.label;
              return (
                <div key={g.key} className="mail-group">
                  <button
                    type="button"
                    className="mail-group-head"
                    aria-expanded={!off}
                    onClick={() => toggleGroup(g.key)}
                  >
                    <ChevronDown
                      size={12}
                      aria-hidden
                      className="mail-group-tw"
                      style={off ? { transform: 'rotate(-90deg)' } : undefined}
                    />
                    <span className="mail-group-name">{name}</span>
                    <span className="mail-group-count">{g.items.length}{countSuffix}</span>
                  </button>
                  {!off && g.items.map((m) => (
                    <MailItem
                      key={m.id}
                      message={m}
                      active={m.id === selectedMailId}
                      onSearchPerson={searchByPerson}
                      onAddContact={(email, name) => setContactAdd({ email, name })}
                      onComposeTo={(to) => goCompose({ prefill: { to: [externalChip(to)] } })}
                      onOpenContact={() => navigate('/contacts')}
                      onSelect={() => { if (!drag.shouldSwallowClick()) openMail(m.id); }}
                      simplify={mailPrefs.simplifySubject}
                      preview={wideDensity}
                      checked={picked.has(m.id)}
                      onToggleCheck={() => togglePick(m.id)}
                      onCheckPointerDown={drag.onCheckPointerDown}
                      rowBusy={rowBusy === m.id}
                      onReply={handleRowReply}
                      onPopout={handleRowPopout}
                      onTrash={(x) => { void handleRowTrash(x); }}
                      onToggleRead={(x) => { void handleRowToggleRead(x); }}
                      onToggleStar={(x) => { void handleRowToggleStar(x); }}
                      onMove={serverNew ? (x) => setMovePicker({ ids: [x.id] }) : undefined}
                      mode={rowMode}
                      onRestore={(x) => { void handleRowRestore(x); }}
                      onPurge={serverNew ? (x) => setPurgeAsk([x.id]) : undefined}
                    />
                  ))}
                </div>
              );
            })}
        </div>

        {/* 스크롤 대신 쪽 번호 — 받아 온 것을 자르고, 모자라면 넘길 때 더 받아 온다. */}
        {visibleMessages.length > 0 && (
          <MailPager
            page={page}
            pageSize={view.pageSize as MailPageSize}
            /* 서버가 세 줬으면 그 값이 진짜다 — 받아 온 것으로 세면 `+` 를 붙여야 한다. */
            loaded={listTotal ?? visibleMessages.length}
            shown={pagedMessages.length}
            hasMore={listTotal === null && nextCursor !== null}
            busy={loadingMore}
            onPage={(n) => { void goToPage(n); }}
            onPageSize={(size) => { applyView({ pageSize: size }); setPage(1); }}
          />
        )}
      </section>

      {detailState === 'open' && view.openMode !== 'full' && (
        <MailSplitter
          orientation={view.openMode === 'bottom' ? 'horizontal' : 'vertical'}
          ratio={view.openMode === 'bottom' ? view.bottomRatio : view.sideRatio}
          onRatio={previewRatio}
          onCommit={commitRatio}
          onResizing={setResizing}
        />
      )}

      <MailDetail
        mailId={selectedMailId}
        onLocalUpdate={handleLocalUpdate}
        onCompose={(prefill, mode) => goCompose({ prefill, badge: mode })}
        isFull={detailState === 'full'}
        onToggleFull={toggleFull}
        onClose={closeDetail}
        // 휴지통·스팸으로 치웠으면 상세를 닫는다 — 방금 버린 메일을 계속 읽고 있을 이유가 없다.
        onMovedAway={closeDetail}
        onOpenContacts={() => navigate('/contacts')}
      />
      </>}
      </div>
      </div>

      {/*
        주소록에 담기 (2026-09-08) — **목록에서 이름을 눌러 담는 자리**.
        ★ 화면을 옮기지 않는다. 상세가 쓰는 것과 **같은 팝업**을 한 줄짜리로 연다.
      */}
      {contactAdd && (
        <ContactPickModal
          candidates={[{ email: contactAdd.email, name: contactAdd.name }]}
          canShare={canShareContacts}
          onClose={() => setContactAdd(null)}
          onDone={() => setContactAdd(null)}
        />
      )}

      {/*
        ★ 2026-08-23 — 여러 통을 치우거나 스팸으로 보낼 때 **가운데 뜨는 팝업**으로 묻는다
          (사용자 지시). 여태 목록 안에 끼어 있는 띠라, 목록을 보고 있던 눈에는 안 띄었다.
        ★ 창은 폴더 비우기·상세 치우기가 쓰던 그 창(MailConfirmDialog)이다. 스팸의 "이 사람도
          막을까" 한 칸만 `extra` 로 끼운다 — 창을 두 벌로 만들면 한쪽만 고쳐진다.
      */}
      <MailConfirmDialog
        open={trashAsk}
        busy={bulkBusy}
        title={tMail('mail.bulk.trash_confirm_title')}
        description={tMail('mail.bulk.trash_confirm_desc').replace('{n}', String(picked.size))}
        confirmLabel={tMail('mail.detail.confirm_trash_ok')}
        onConfirm={() => { void runBulk({ isTrash: true }); }}
        onCancel={() => setTrashAsk(false)}
      />

      {/* 영구삭제는 되돌릴 수 없다 — 반드시 한 번 묻는다(폴더 비우기와 같은 규칙). */}
      <MailConfirmDialog
        open={purgeAsk !== null}
        danger
        busy={bulkBusy}
        title={tMail('mail.bulk.purge_confirm_title')}
        description={tMail('mail.bulk.purge_confirm_desc').replace('{n}', String(purgeAsk?.length ?? 0))}
        confirmLabel={tMail('mail.bulk.purge')}
        onConfirm={() => { void runPurge(purgeAsk ?? []); }}
        onCancel={() => setPurgeAsk(null)}
      />

      {/* 처리 결과 알림 — 목록은 이 창 뒤에서 이미 다시 받아 왔다. */}
      <MailConfirmDialog
        open={doneNotice !== null}
        noticeOnly
        title={doneNotice?.title ?? ''}
        description={doneNotice?.desc ?? ''}
        confirmLabel={tMail('mail.bulk.notice_ok')}
        onConfirm={() => setDoneNotice(null)}
        onCancel={() => setDoneNotice(null)}
      />

      <MailConfirmDialog
        open={spamAsk}
        busy={bulkBusy}
        title={tMail('mail.spam.confirm_title')}
        description={tMail('mail.bulk.spam_confirm_desc').replace('{n}', String(picked.size))}
        confirmLabel={tMail('mail.spam.confirm_ok')}
        extra={(
          /* 한 통을 치우는 것과 그 사람을 막는 것은 다른 일이라 따로 묻는다. */
          <label className="mail-confirm-check">
            <input type="checkbox" checked={blockSender} onChange={(e) => setBlockSender(e.target.checked)} />
            <span>{tMail('mail.spam.block_sender')}</span>
          </label>
        )}
        onConfirm={() => { void runBulk({ isSpam: true, blockSender }); }}
        onCancel={() => setSpamAsk(false)}
      />

      <MailConfirmDialog
        open={confirm !== null}
        danger
        busy={confirmBusy}
        title={confirm === null ? '' : (confirm.kind === 'delete'
          ? tMail('mail.confirm.delete_title')
          : (confirm.permanent ? tMail('mail.confirm.empty_trash_title') : tMail('mail.confirm.empty_title')))}
        description={confirm === null ? '' : (confirm.kind === 'delete'
          ? tMail('mail.confirm.delete_desc')
          : (confirm.permanent ? tMail('mail.confirm.empty_trash_desc') : tMail('mail.confirm.empty_desc')))}
        confirmLabel={confirm?.kind === 'delete' ? tMail('mail.confirm.delete_ok') : tMail('mail.confirm.empty_ok')}
        onConfirm={() => { void runConfirm(); }}
        onCancel={() => setConfirm(null)}
      />

      {/* 어느 메일함으로 옮길까 — 내가 만든 메일함만 나온다(기본 메일함은 옮길 자리가 아니다). */}
      {movePicker !== null && (
        <div
          className="mail-confirm-backdrop"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setMovePicker(null); }}
        >
          <div className="mail-move" role="dialog" aria-modal="true" aria-label={tMail('mail.folder.move_aria')}>
            <p className="mail-confirm-title">{tMail('mail.folder.move_aria')}</p>
            {folders.length === 0 ? (
              <p className="mail-confirm-desc">{tMail('mail.folder.no_folders')}</p>
            ) : (
              <ul className="mail-move-list">
                {folders.map((f) => (
                  <li key={f.id}>
                    <button type="button" onClick={() => doMove(f.id)}>
                      {f.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {moveSender && (
              <div className="mail-move-bysender">
                <Checkbox
                  checked={moveBySender}
                  onChange={(e) => setMoveBySender(e.target.checked)}
                  label={tMail('mail.folder.move_by_sender').replace(
                    '{who}',
                    moveSender.name && moveSender.name !== moveSender.email
                      ? `${moveSender.name} <${moveSender.email}>`
                      : moveSender.email,
                  )}
                />
                {moveBySender && <p className="mail-move-bysender__hint">{tMail('mail.folder.move_by_sender_hint')}</p>}
              </div>
            )}
            <div className="mail-confirm-actions">
              <button type="button" className="mail-confirm-btn" onClick={() => doMove(null)}>
                {tMail('mail.folder.move_inbox')}
              </button>
              <button type="button" className="mail-confirm-btn" onClick={() => setMovePicker(null)}>
                {tMail('mail.spam.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <MailPopout
        mailId={popout?.id ?? null}
        title={popout?.title ?? ''}
        onClose={() => setPopout(null)}
        onLocalUpdate={handleLocalUpdate}
        onCompose={(prefill, mode) => goCompose({ prefill, badge: mode })}
      />

      {/*
        「팝업쓰기」로 연 작성 창 (2026-09-05). 기본값은 「일반쓰기」라 여기 아무것도 안 뜬다.
        ★ 보낸 뒤 폴더를 옮기는 것은 화면판이 주소로 하던 일이다 — 창은 같은 화면 안이라
          곧장 옮기고 목록을 다시 읽는다.
      */}
      {composePop && (
        <ComposeModal
          open
          prefill={composePop.prefill}
          onClose={() => setComposePop(null)}
          onSent={(res) => {
            setComposePop(null);
            setFolder(res.isDraft ? 'draft' : 'sent');
            setActiveFolderId(null);
            setPage(1);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

    </div>
  );
}

function MailItem({
  message, active, onSelect, simplify, preview, checked, onToggleCheck,
  onCheckPointerDown, rowBusy, onReply, onPopout, onTrash, onToggleRead, onToggleStar, onMove,
  mode = 'normal', onRestore, onPurge, onSearchPerson, onAddContact, onComposeTo, onOpenContact,
}: {
  message: MailListItem; active: boolean; onSelect: () => void; simplify: boolean; preview: boolean;
  checked: boolean; onToggleCheck: () => void;
  onCheckPointerDown: (e: React.PointerEvent, id: string) => void;
  rowBusy: boolean;
  /**
   * 어느 메일함을 보고 있나 (2026-08-26). 치워 둔 함에서는 줄 위 아이콘이 달라지고,
   * 휴지통에서는 **즐겨찾기(별)를 아예 안 그린다** — 버린 편지에 별을 달 이유가 없다(사용자 지시).
   */
  mode?: MailRowMode;
  onRestore?: (m: MailListItem) => void;
  onPurge?: (m: MailListItem) => void;
  onToggleStar: (m: MailListItem) => void;
  onReply: (m: MailListItem) => void;
  onPopout: (m: MailListItem) => void;
  onTrash: (m: MailListItem) => void;
  onToggleRead: (m: MailListItem) => void;
  /** 옮길 메일함이 있을 때만 온다 — 없으면 버튼을 안 그린다. */
  onMove?: (m: MailListItem) => void;
  /**
   * 보낸사람 이름을 눌러 사람 카드를 열 때 쓰는 손잡이들 (2026-09-08 · 정본 F3-2).
   * ★ **선택**이다. 안 주면 이름이 지금까지처럼 그냥 글자다(회귀 0).
   */
  onSearchPerson?: (email: string, dir: 'from' | 'to') => void;
  /**
   * 「주소록에 추가」 — **이 화면에서** 팝업을 연다(2026-09-08 사용자 지적).
   * ★ 처음엔 `/contacts?add=…` 로 **화면을 옮겼는데**, 메일을 보다가 담는 일이라
   *   목록에서 튕겨 나가면 하던 일이 끊긴다. 담기는 **보던 자리에서** 끝나야 한다.
   */
  onAddContact?: (email: string, name: string | null) => void;
  /** 「메일 보내기」 — 부모의 작성 화면을 연다(여기서도 화면을 새로 띄우지 않는다). */
  onComposeTo?: (email: string) => void;
  /** 「연락처 열기」 — 이것만 주소록으로 **간다**(가는 것이 목적인 행동이다). */
  onOpenContact?: () => void;
}) {
  // Cycle 54 — AI 비서 sender 매칭 — backend sender name 은 fixed 'AI 비서' (ko hardcoded by AI service).
  // 향후 backend 가 lang-aware sender name 보내면 별도 prop 으로 변경.
  const aiName = tMail('mail.sender.ai_assistant');
  const isAi = message.sender.name === aiName || message.sender.name === 'AI 비서';
  const initials = (message.sender.name || '?').slice(0, 1);
  const time = formatMailTime(message.sentAt);

  return (
    // ★ 체크 칸은 버튼의 **형제**다 — <button> 안에 <input> 을 넣으면 파서가 바깥 버튼을 먼저 닫아
    //   줄 전체가 깨진다. 줄을 감싸는 상자를 하나 두고 나란히 세운다.
    <div
      className={`mail-item-row${checked ? ' mail-item-row--checked' : ''}${active ? ' mail-item-row--active' : ''}`}
      {...{ [DRAG_ROW_ATTR]: message.id }}
      data-picked={checked ? 'true' : 'false'}
    >
      {/*
        ★ 2026-08-23 — 선택을 바꾸는 길을 **하나로** 줄였다.

        여태 이 칸에는 길이 둘이었다 — `onPointerDown`(끌어 고르기)과 체크박스의 `onChange`.
        둘이 부딪히지 않게 `e.detail > 0` 일 때 `preventDefault` 로 막았는데, 그 막이 **어디를
        눌렀느냐(작은 네모냐 둘레 여백이냐)와 브라우저·입력장치**에 따라 다르게 걸린다.
        사용자에게는 "눌러도 안 풀리거나 엉뚱하게 동작하는" 것으로 보인다.

        이제 **누르는 일은 pointerdown 한 곳**이 한다. 안의 `<input>` 은 그림일 뿐이고
        (`readOnly` · 초점 순서 밖), 칸 자체가 체크박스 노릇을 한다 — 키보드는 Space/Enter 로
        받는다(마우스가 없는 사람에게 길이 사라지면 안 된다).
      */}
      <label
        className="mail-item-check"
        role="checkbox"
        aria-checked={checked}
        aria-label={`${message.sender.name} · ${message.subject} ${tMail('mail.bulk.aria_pick')}`}
        tabIndex={0}
        onPointerDown={(e) => onCheckPointerDown(e, message.id)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key !== ' ' && e.key !== 'Enter') return;
          e.preventDefault();
          e.stopPropagation();
          onToggleCheck();
        }}
      >
        <input type="checkbox" checked={checked} readOnly tabIndex={-1} aria-hidden />
      </label>
      {/*
        ★ 별은 **진짜 버튼**이다. 여태 장식용 svg 라 눌러도 별이 아니라 줄이 눌렸고,
          그래서 "즐겨찾기를 누르면 상세보기로 넘어간다" 가 됐다.
        ★ 줄 버튼 **안에** 넣으면 파서가 바깥 button 을 먼저 닫아 줄이 통째로 깨진다 —
          체크 칸과 같은 이유로 **형제**로 세운다. 자리·모양은 CSS 가 그대로 지킨다.
      */}
      {/* 휴지통에서는 별을 안 그린다 — 버린 편지의 즐겨찾기는 뜻이 없다(2026-08-26 사용자 지시). */}
      {mode !== 'trash' && (
      <button
        type="button"
        className="mail-item-star-btn"
        aria-label={message.isStarred ? tMail('mail.row.unstar') : tMail('mail.row.star')}
        aria-pressed={message.isStarred}
        title={message.isStarred ? tMail('mail.row.unstar') : tMail('mail.row.star')}
        disabled={rowBusy}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleStar(message); }}
      >
        <Star
          size={16}
          className={`mail-item-star${message.isStarred ? ' mail-item-star--active' : ''}`}
          fill={message.isStarred ? 'currentColor' : 'none'}
          aria-hidden
        />
      </button>
      )}
      <button
        type="button"
        className={`mail-item${message.isRead ? '' : ' mail-item--unread'}${active ? ' mail-item--active' : ''}`}
        onClick={onSelect}
        aria-pressed={active}
        aria-label={`${message.sender.name} · ${message.subject}`}
      >
      <div className={`mail-item-avatar${isAi ? ' mail-item-avatar--ai' : ''}`} aria-hidden>
        {isAi ? <Bot size={16} /> : initials}
      </div>
      <div className="mail-item-meta">
        <div className="mail-item-meta-row">
          {/*
            ★ 이름을 누르면 사람 카드(F3-2)가 뜬다.
            ⚠ **행 전체가 이미 `<button>`** 이라 그 안에 버튼을 넣을 수 없다 — 위 체크 칸이
              형제로 나가 있는 것과 같은 이유다(파서가 바깥 버튼을 먼저 닫는다). 여기는
              자리가 행 안쪽 깊이라 형제로 뺄 수 없어 `role="button"` 인 span 으로 두고
              **클릭을 멈춰 세운다** — 행 열기는 그대로 살아 있다.
          */}
          <PersonCard
            email={message.sender.email}
            name={message.sender.name}
            onSendMail={(to) => onComposeTo?.(to)}
            onAdd={(email, name) => onAddContact?.(email, name)}
            onOpenContact={() => onOpenContact?.()}
            onSearchFrom={onSearchPerson ? (em) => onSearchPerson(em, 'from') : undefined}
            onSearchTo={onSearchPerson ? (em) => onSearchPerson(em, 'to') : undefined}
          >
            {({ ref, toggle, open }) => (
              <span
                ref={ref as React.RefObject<HTMLSpanElement>}
                className="mail-item-sender mail-item-sender--pick"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={(e) => { e.stopPropagation(); toggle(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggle(); }
                }}
              >
                {message.sender.name}
              </span>
            )}
          </PersonCard>
          {/* 수신확인 (2026-08-21) — 내가 보낸 메일에만 붙는다. 받은 메일에는 칸 자체가 없다. */}
          {message.readCount !== undefined && message.recipientCount > 0 && (
            <span
              className={`mail-item-receipt${message.readCount >= message.recipientCount ? ' mail-item-receipt--all' : ''}`}
              title={tMail('mail.receipt.heading')}
            >
              {tMail('mail.receipt.read_of')
                .replace('{a}', String(message.readCount))
                .replace('{b}', String(message.recipientCount))}
            </span>
          )}
          {time && <span className="mail-item-time">{time}</span>}
        </div>
        {/*
          ★ 2026-08-22 — 제목 줄이 칸을 다 쓴다.
            넓은 목록에서는 본문 미리보기가 제목 **바로 뒤에** `— 본문…` 으로 붙어, 제목이
            칸의 52% 에서 잘렸다(제목이 긴 메일일수록 정작 제목이 안 보였다). 미리보기는
            기본으로 끄고, 비운 자리에는 글자 대신 **폭이 고정된 표식**만 둔다.
          ★ 표식은 제목 줄 **뒤**에 태운다 — 따로 줄을 만들면 좁은 카드가 다시 세 줄이 되어
            아무것도 못 번다(높이가 그대로다).
        */}
        <div className={`mail-item-subject${preview ? '' : ' mail-item-subject--wide'}`}>
          <span className="mail-item-subject-t">
            {(message.subject
              ? (simplify ? simplifySubject(message.subject) : message.subject)
              : tMail('mail.item.subject_empty'))}
          </span>
          {/* 첨부 개수 — 서버가 새것일 때만 온다(구 서버는 이 칸을 모른다). */}
          {message.attachmentCount !== undefined && message.attachmentCount > 0 && (
            <span
              className="mail-item-flag mail-item-flag--clip"
              title={tMail('mail.item.flag_attach_title')}
            >
              <Paperclip size={10} aria-hidden />
              {message.attachmentCount}
            </span>
          )}
          {/*
            붙은 태그 (migration 161) — 자동분류가 붙인 것이 여기 보인다.
            ★★ **보이지 않는 태그는 안 붙인 것과 같다.** 자동분류의 「태그 적용」이 실제로
               무언가 하려면 목록에서 읽혀야 한다. 칩은 태그 화면과 **같은 모양**이다(정본 B12).
            ★ 구 서버는 이 칸을 아예 안 준다 → 아무것도 안 그린다(무회귀).
          */}
          {message.tags?.map((t) => (
            <span key={t.id} className="mailtag mailtag--sm" data-tagcolor={t.color}>{t.name}</span>
          ))}
          {/* 참조로 받은 줄 — 내가 보낸 메일에는 서버가 안 준다. */}
          {message.myType === 'CC' && (
            <span className="mail-item-flag mail-item-flag--cc" title={tMail('mail.item.flag_cc_title')}>
              {tMail('mail.field.cc')}
            </span>
          )}
          {/* 외부 메일 가져오기(migration 112) — 사내 메일과 한 목록에 섞이므로 출처를 밝힌다.
              ★ 여태 보낸사람 줄에 있었는데, 넓은 목록에서는 CSS 가 통째로 감췄다(칸이 좁아서).
                제목 줄로 옮기면 어느 폭에서든 보인다. */}
          {message.sender.isExternal && (
            <span className="mail-item-flag mail-item-flag--ext" title={tMail('mail.sender.external_title')}>
              {tMail('mail.sender.external_badge')}
            </span>
          )}
        </div>
        {preview && (
          <div className="mail-item-preview">{message.bodyPreview || tMail('mail.item.body_empty')}</div>
        )}
      </div>
        {!message.isRead && <div className="mail-item-unread-dot" aria-label={tMail('mail.item.aria_unread')} />}
      </button>
      <MailRowActions
        message={message}
        busy={rowBusy}
        mode={mode}
        onReply={onReply}
        onPopout={onPopout}
        onTrash={onTrash}
        onToggleRead={onToggleRead}
        onMove={onMove}
        onRestore={onRestore}
        onPurge={onPurge}
      />
    </div>
  );
}

function formatMailTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export default MailPage;
